import axios from 'axios';

import { AXIOS_CONFIG } from '../config/api';
import {
  type ServiceCategoryConflictCode,
  conflictCodeFromResponse,
  unprocessableMessage,
} from '../constants/serviceCategories';
import type { EnquiryDraft } from '../hooks/useConciergeEnquiryForm';
import type { PartnerApplicationDraft } from '../pages/JoinPage';
import {
  Accommodation,
  AccommodationAdmin,
  AccommodationBookingResponse,
  AccommodationCreate,
  AccommodationLocation,
  AccommodationUpdatePayload,
  AccommodationVariantCreate,
  AdminHeroSlot,
  AdminServiceCategory,
  AdminUserListQuery,
  AdminUserPage,
  AnalyticsOverview,
  AvailabilityCalendar,
  BedLine,
  BedroomDetail,
  Booking,
  BookingAdmin,
  BookingChartData,
  BookingDraftStateResponse,
  BookingRequest,
  BookingRequestAdmin,
  BookingRequestApproveResponse,
  BookingRequestCreate,
  BookingRequestQuoteRequest,
  BookingRequestVerifyPaymentResponse,
  Brand,
  BrandCreate,
  BrandSeasonCalendar,
  BrandSeasonCalendarWrite,
  BrandStayFees,
  BrandStayFeesWrite,
  BrandUpdate,
  CartCheckoutRequest,
  CartCheckoutResponse,
  CheckoutSessionVerifyResponse,
  CompanyReviewsResponse,
  DeltaPaymentIntentResponse,
  EligibleRatePlan,
  EnquiryCreateRequest,
  EnquiryResponse,
  ExternalReview,
  FilterOptions,
  HeroSlotName,
  HeroSlotUpdate,
  ImageUploadResponse,
  InclusionPreset,
  InclusionPresetQuery,
  InclusionPresetUpdate,
  InclusionPresetWrite,
  LocationSuggestion,
  LocationSuggestionKind,
  ModifierAdmin,
  ModifierCreatePayload,
  ModifierGroup,
  ModifierGroupAdmin,
  ModifierGroupCreatePayload,
  ModifierGroupUpdatePayload,
  ModifierUpdatePayload,
  NextPaxBookingConfirmRequest,
  NextPaxBookingDraftCreate,
  NextPaxBookingDraftResponse,
  NextPaxDraftGuestFieldsUpdate,
  Notification,
  Package,
  PackageCreate,
  PackageUpdate,
  PackageVariantCreate,
  PartnerApplicationCreateRequest,
  PartnerApplicationResponse,
  PaymentIntentResponse,
  PriceQuote,
  PublicHeroPicks,
  PublicServiceCategory,
  QuoteGroup,
  QuoteGroupAdmin,
  QuoteGroupQuoteRequest,
  QuoteGroupVerifyPaymentResponse,
  RateBreakdown,
  RecoveryIntentResponse,
  RetryPaymentResponse,
  Review,
  ReviewCreate,
  ReviewStats,
  Service,
  ServiceBookableWindow,
  ServiceBookableWindowPayload,
  ServiceBookableWindows,
  ServiceCategoryCreate,
  ServiceCategoryUpdate,
  ServiceChildSummary,
  ServiceCreate,
  ServiceOccurrence,
  ServiceOccurrenceAdmin,
  ServiceOccurrencePayload,
  ServiceOccurrenceRemoval,
  ServiceUpdatePayload,
  ServiceVariantCreate,
  SettleRecoveryResponse,
  SetupIntentResponse,
  TaxProfile,
  TaxProfileWrite,
  User,
  UserPreferences,
  UserPreferencesUpdate,
  UserUpdate,
  UserWithStats,
  WishlistItem,
} from '../types';
import type {
  BlogDraftCostSummary,
  BlogDraftRun,
  BlogPost,
  BlogPostAdmin,
  BlogPostAdminSummary,
  BlogPostPage,
  BlogPostPreview,
  BlogPostUpdate,
  BlogTopic,
  BlogTopicCreate,
} from '../types/blog';
import { signalSessionExpired } from '../utils/sessionExpiry';
import type { ServiceSearchParams } from '../utils/tripSearchResults';
import cognitoAuthService, { TOO_MANY_ATTEMPTS_MESSAGE } from './cognitoAuthService';

const apiClient = axios.create(AXIOS_CONFIG);

// Add Cognito auth token to requests
apiClient.interceptors.request.use((config) => {
  try {
    // Use synchronous token getter to avoid delays during login flow
    const token = cognitoAuthService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.error('ApiService: Failed to get access token:', error);
  }
  return config;
});

// Add response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if it's an auth error and we haven't already tried to refresh
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      const wasSentWithBearer = Boolean(originalRequest.headers?.Authorization);

      let hasSessionEnded = false;
      try {
        // Try to get a fresh token
        const newToken = await cognitoAuthService.getValidAccessToken();
        if (newToken) {
          // Update the authorization header and retry the request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        }
        hasSessionEnded = true;
      } catch (refreshError) {
        console.error('ApiService: Token refresh failed, ending the session:', refreshError);
        hasSessionEnded = true;
      }
      // A guest request has no session to end.
      if (hasSessionEnded && wasSentWithBearer) {
        cognitoAuthService.logout();
        signalSessionExpired();
      }
    }

    return Promise.reject(error);
  },
);

// Note: Authentication is now handled by cognitoAuthService
// These old endpoints are removed in favor of Cognito authentication

export const getInclusionPresets = async (
  query: InclusionPresetQuery = {},
): Promise<InclusionPreset[]> => {
  const response = await apiClient.get<InclusionPreset[]>('/api/v1/admin/inclusion-presets/', {
    params: query,
  });
  return response.data;
};

export const createInclusionPreset = async (
  pair: InclusionPresetWrite,
): Promise<InclusionPreset> => {
  const response = await apiClient.post<InclusionPreset>('/api/v1/admin/inclusion-presets/', pair);
  return response.data;
};

export const updateInclusionPreset = async (
  presetId: number,
  pair: InclusionPresetUpdate,
): Promise<InclusionPreset> => {
  const response = await apiClient.patch<InclusionPreset>(
    `/api/v1/admin/inclusion-presets/${presetId}`,
    pair,
  );
  return response.data;
};

export const deleteInclusionPreset = async (
  presetId: number,
  expectedVersion: number,
): Promise<void> => {
  await apiClient.delete(`/api/v1/admin/inclusion-presets/${presetId}`, {
    params: { expected_version: expectedVersion },
  });
};

// User Profile Endpoints
export const updateUserProfile = async (profileData: UserUpdate): Promise<User> => {
  const response = await apiClient.put('/api/v1/users/profile', profileData);
  return response.data;
};

export const getUserProfile = async (): Promise<User> => {
  const response = await apiClient.get('/api/v1/users/profile');
  return response.data;
};

export type AdminMfaSignInMethod = 'password' | 'federated';

export interface AdminMfaStatus {
  required: boolean;
  enrolled: boolean;
  sign_in_method: AdminMfaSignInMethod;
}

export interface TotpSetup {
  secret_code: string;
  otpauth_uri: string;
}

/** The setup code was wrong or stale; the admin can enter a new one. */
export class TotpSetupCodeRejectedError extends Error {}

/** The code was right and two-factor is on, but ending the old sessions failed. */
export class TotpSetupSignOutFailedError extends Error {}

const ADMIN_MFA_PATH = '/api/v1/auth/cognito/mfa';
const TOTP_SETUP_CODE_INVALID = 'mfa_code_invalid';
const TOTP_SETUP_SIGN_OUT_FAILED = 'mfa_sign_out_failed';
export const TOTP_SETUP_FAILED_MESSAGE = 'Two-factor setup failed. Please try again.';

const toTotpSetupError = (error: unknown): Error => {
  if (!axios.isAxiosError(error)) return new Error(TOTP_SETUP_FAILED_MESSAGE);
  if (error.response?.status === 429) return new Error(TOO_MANY_ATTEMPTS_MESSAGE);
  const body: unknown = error.response?.data;
  const detail =
    typeof body === 'object' && body !== null && 'detail' in body && typeof body.detail === 'string'
      ? body.detail
      : '';
  const code =
    typeof body === 'object' && body !== null && 'code' in body && typeof body.code === 'string'
      ? body.code
      : '';
  const message = detail || TOTP_SETUP_FAILED_MESSAGE;
  if (code === TOTP_SETUP_CODE_INVALID) return new TotpSetupCodeRejectedError(message);
  if (code === TOTP_SETUP_SIGN_OUT_FAILED) return new TotpSetupSignOutFailedError(message);
  return new Error(message);
};

const isAdminMfaStatus = (data: unknown): data is AdminMfaStatus =>
  typeof data === 'object' &&
  data !== null &&
  'required' in data &&
  typeof data.required === 'boolean' &&
  'enrolled' in data &&
  typeof data.enrolled === 'boolean' &&
  'sign_in_method' in data &&
  (data.sign_in_method === 'password' || data.sign_in_method === 'federated');

const isTotpSetup = (data: unknown): data is TotpSetup =>
  typeof data === 'object' &&
  data !== null &&
  'secret_code' in data &&
  typeof data.secret_code === 'string' &&
  'otpauth_uri' in data &&
  typeof data.otpauth_uri === 'string';

export const getAdminMfaStatus = async (): Promise<AdminMfaStatus> => {
  const response = await apiClient.get(`${ADMIN_MFA_PATH}/status`);
  const body: unknown = response.data;
  if (!isAdminMfaStatus(body)) throw new Error('Unexpected two-factor status response');
  return body;
};

export const startTotpSetup = async (): Promise<TotpSetup> => {
  try {
    const response = await apiClient.post(`${ADMIN_MFA_PATH}/totp/setup`);
    const body: unknown = response.data;
    if (!isTotpSetup(body)) throw new Error(TOTP_SETUP_FAILED_MESSAGE);
    return body;
  } catch (error: unknown) {
    throw toTotpSetupError(error);
  }
};

export const verifyTotpSetup = async (code: string): Promise<void> => {
  try {
    await apiClient.post(`${ADMIN_MFA_PATH}/totp/verify`, { code });
  } catch (error: unknown) {
    throw toTotpSetupError(error);
  }
};

// Literal the backend requires in the erasure request body (schemas/user.py
// AccountEraseRequest.confirmation). The dialog's typed-confirmation gate
// compares against this same constant so the two can't silently diverge.
export const DELETE_ACCOUNT_CONFIRMATION = 'DELETE';

export const deleteAccount = async (): Promise<void> => {
  await apiClient.delete('/api/v1/users/me', {
    data: { confirmation: DELETE_ACCOUNT_CONFIRMATION },
  });
};

// User Preferences Endpoints
export const getUserPreferences = async (): Promise<UserPreferences> => {
  const response = await apiClient.get('/api/v1/users/preferences');
  return response.data;
};

export const updateUserPreferences = async (
  preferences: UserPreferencesUpdate,
): Promise<UserPreferences> => {
  const response = await apiClient.put('/api/v1/users/preferences', preferences);
  return response.data;
};

// Profile Picture Endpoints
export const uploadProfilePicture = async (
  file: File,
): Promise<{ message: string; profile_picture_url: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post('/api/v1/users/profile-picture', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteProfilePicture = async (): Promise<{ message: string }> => {
  const response = await apiClient.delete('/api/v1/users/profile-picture');
  return response.data;
};

// Accommodation Endpoints
export interface AccommodationDraftPriceProjectionRequest {
  base_price_per_night: number;
  vendor_tax_percentage: number;
  platform_markup_percentage: number;
}

export interface AccommodationDraftPriceProjectionResponse {
  display_price_cents: number;
}

export const projectAccommodationDraftPrice = async (
  draftPrice: AccommodationDraftPriceProjectionRequest,
): Promise<AccommodationDraftPriceProjectionResponse> => {
  const response = await apiClient.post<AccommodationDraftPriceProjectionResponse>(
    '/api/v1/accommodations/draft-price-projection',
    draftPrice,
  );
  return response.data;
};

export const getAccommodations = async (params?: {
  category?: string;
  accommodation_type?: string;
  location?: string;
  min_guests?: number;
  search?: string;
  sort_by?: string;
  order?: 'asc' | 'desc';
  shuffle_seed?: number;
  check_in?: string;
  check_out?: string;
}): Promise<Accommodation[]> => {
  try {
    const response = await apiClient.get('/api/v1/accommodations/', { params });

    if (!response.data || !Array.isArray(response.data)) {
      return [];
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching accommodations:', error);
    throw error;
  }
};

export const getAccommodationTypes = async (): Promise<string[]> => {
  const response = await apiClient.get('/api/v1/accommodations/types');
  return response.data;
};

export const getAccommodation = async (id: number): Promise<Accommodation> => {
  const response = await apiClient.get(`/api/v1/accommodations/${id}`);
  return response.data;
};

export const getAccommodationBySlug = async (slug: string): Promise<Accommodation> => {
  const response = await apiClient.get(
    `/api/v1/accommodations/by-slug/${encodeURIComponent(slug)}`,
  );
  return response.data;
};

// Admin-only parent read: the public detail route omits the private address
// and property-manager fields this one carries.
export const getAdminAccommodation = async (id: number): Promise<AccommodationAdmin> => {
  const response = await apiClient.get(`/api/v1/accommodations/${id}/admin`);
  return response.data;
};

// Returns a parent accommodation's live child variants — full accommodation
// rows (carrying amenity_details, images, everything) via
// GET /accommodations/{id}/variants, ordered (display_order, id) by the
// backend. Unlike the parent's own `children` embed
// (AccommodationChildSummary), these full rows carry amenity data, which
// edit-form variant hydration needs to name-match against the catalog (see
// accommodationDraft.ts's resolveAmenitySelection). Each row goes through
// the same normalization getAccommodation applies, since these are full
// accommodation records too.
export const getAccommodationVariants = async (
  accommodationId: number,
): Promise<Accommodation[]> => {
  const response = await apiClient.get(`/api/v1/accommodations/${accommodationId}/variants`);
  return response.data;
};

// FastAPI's `Query(default_factory=list)` reads a bare repeated-key array
// (`foo=a&foo=b`), not axios's default bracketed form (`foo[]=a&foo[]=b`).
// This override makes axios emit the bare form. Shared by every GET
// wrapper below that accepts an optional repeated-key array param
// (`selected_fee_codes`, `selected_modifiers`) so the serialiser can't
// drift between call sites.
const REPEATED_KEY_PARAMS_SERIALIZER = { indexes: null };

// Attaches `paramValues` under `paramKey` on `baseParams` only when
// non-empty, so a caller that doesn't opt into the array param keeps the
// wire shape byte-identical to before the param existed. Returns the
// params object plus the paramsSerializer override the caller spreads into
// its axios request config.
const withRepeatedKeyArrayParam = (
  baseParams: Record<string, unknown>,
  paramKey: string,
  paramValues: readonly (string | number)[] | undefined,
): { params: Record<string, unknown>; paramsSerializer: { indexes: null } } => ({
  params:
    paramValues && paramValues.length > 0 ? { ...baseParams, [paramKey]: paramValues } : baseParams,
  paramsSerializer: REPEATED_KEY_PARAMS_SERIALIZER,
});

export interface RateBreakdownParams {
  check_in: string;
  check_out: string;
  adults: number;
  children?: number;
  // Story 4.1.7 slice 4 — conditional fee filtering. Backend defaults to
  // 0 (no conditional fees applied). Frontend stepper values map 1:1.
  pets?: number;
  vehicles?: number;
  extra_beds?: number;
  // DB primary key of the selected RatePlan (`EligibleRatePlan.id`).
  // The backend resolves the plan server-side and reads its
  // `pricing_type` + `pricing_offset` from the DB so a manipulated
  // client can't quote itself an arbitrary discount. Optional: a
  // property with no eligible rate plans is priced at its base rate, and
  // the backend returns a base-price breakdown when the param is omitted.
  rate_plan_id?: number;
  // Opt-in OPT-UNCONDITIONAL fee codes (e.g. `["WIF", "LCO"]`). Sent as
  // repeated `selected_fee_codes=` query keys (axios default for arrays).
  // When empty/undefined the param is omitted so the wire shape stays
  // byte-identical for callers that don't opt in.
  selected_fee_codes?: string[];
}

export const getRateBreakdown = async (
  accommodationId: number,
  params: RateBreakdownParams,
): Promise<RateBreakdown> => {
  const baseParams: Record<string, unknown> = {
    check_in: params.check_in,
    check_out: params.check_out,
    adults: params.adults,
    children: params.children ?? 0,
    pets: params.pets ?? 0,
    vehicles: params.vehicles ?? 0,
    extra_beds: params.extra_beds ?? 0,
  };
  // Only attach rate_plan_id when a plan is selected. A property with no
  // eligible rate plans omits it entirely so the backend returns the
  // base-price breakdown.
  if (params.rate_plan_id != null) {
    baseParams.rate_plan_id = params.rate_plan_id;
  }
  const response = await apiClient.get(
    `/api/v1/accommodations/${accommodationId}/rate-breakdown`,
    withRepeatedKeyArrayParam(baseParams, 'selected_fee_codes', params.selected_fee_codes),
  );
  return response.data;
};

// Returns the effective modifier-group catalogue for a native
// accommodation (active groups, active modifiers nested, ordered
// (display_order, id) by the backend). `[]` when the listing has none.
export const getAccommodationModifierGroups = async (
  accommodationId: number,
): Promise<ModifierGroup[]> => {
  const response = await apiClient.get(`/api/v1/accommodations/${accommodationId}/modifier-groups`);
  return response.data;
};

// Returns the effective modifier-group catalogue for a native service —
// mirrors getAccommodationModifierGroups for the service listing leg.
export const getServiceModifierGroups = async (serviceId: number): Promise<ModifierGroup[]> => {
  const response = await apiClient.get(`/api/v1/services/${serviceId}/modifier-groups`);
  return response.data;
};

// Admin authoring reads — every group authored for the listing and its
// variants, including inactive groups/modifiers and the raw
// (pre-uplift) price_delta_cents. Distinct from the guest-facing reads
// above, which are active-only and quote-priced.
export const getAccommodationModifierGroupsAdmin = async (
  accommodationId: number,
): Promise<ModifierGroupAdmin[]> => {
  const response = await apiClient.get(
    `/api/v1/accommodations/${accommodationId}/modifier-groups/admin`,
  );
  return response.data;
};

// Fixed-schedule occurrences a guest can book: upcoming and not hidden, in date order.
export const getServiceOccurrences = async (serviceId: number): Promise<ServiceOccurrence[]> => {
  const response = await apiClient.get(`/api/v1/services/${serviceId}/occurrences`);
  return response.data;
};

// Upcoming booking windows of a guest-picks service (public read).
export const getServiceBookableWindows = async (
  serviceId: number,
): Promise<ServiceBookableWindows> => {
  const response = await apiClient.get(`/api/v1/services/${serviceId}/bookable-windows`);
  return response.data;
};

// Fixed-schedule occurrence authoring (admin): the list includes past and hidden rows.
export const getServiceOccurrencesAdmin = async (
  serviceId: number,
): Promise<ServiceOccurrenceAdmin[]> => {
  const response = await apiClient.get(`/api/v1/services/${serviceId}/occurrences/admin`);
  return response.data;
};

export const createServiceOccurrence = async (
  serviceId: number,
  data: ServiceOccurrencePayload,
): Promise<ServiceOccurrenceAdmin> => {
  const response = await apiClient.post(`/api/v1/services/${serviceId}/occurrences`, data);
  return response.data;
};

export const updateServiceOccurrence = async (
  serviceId: number,
  occurrenceId: number,
  data: ServiceOccurrencePayload,
): Promise<ServiceOccurrenceAdmin> => {
  const response = await apiClient.put(
    `/api/v1/services/${serviceId}/occurrences/${occurrenceId}`,
    data,
  );
  return response.data;
};

// Deletes an unbooked occurrence, or hides one that bookings or requests reference.
export const removeServiceOccurrence = async (
  serviceId: number,
  occurrenceId: number,
): Promise<ServiceOccurrenceRemoval> => {
  const response = await apiClient.delete(
    `/api/v1/services/${serviceId}/occurrences/${occurrenceId}`,
  );
  return response.data;
};

// Booking-window authoring (admin): the list includes past windows.
export const getServiceBookableWindowsAdmin = async (
  serviceId: number,
): Promise<ServiceBookableWindow[]> => {
  const response = await apiClient.get(`/api/v1/services/${serviceId}/bookable-windows/admin`);
  return response.data;
};

export const createServiceBookableWindow = async (
  serviceId: number,
  data: ServiceBookableWindowPayload,
): Promise<ServiceBookableWindow> => {
  const response = await apiClient.post(`/api/v1/services/${serviceId}/bookable-windows`, data);
  return response.data;
};

export const updateServiceBookableWindow = async (
  serviceId: number,
  windowId: number,
  data: ServiceBookableWindowPayload,
): Promise<ServiceBookableWindow> => {
  const response = await apiClient.put(
    `/api/v1/services/${serviceId}/bookable-windows/${windowId}`,
    data,
  );
  return response.data;
};

export const removeServiceBookableWindow = async (
  serviceId: number,
  windowId: number,
): Promise<void> => {
  await apiClient.delete(`/api/v1/services/${serviceId}/bookable-windows/${windowId}`);
};

// Admin authoring read twin of getAccommodationModifierGroupsAdmin for the
// service listing leg.
export const getServiceModifierGroupsAdmin = async (
  serviceId: number,
): Promise<ModifierGroupAdmin[]> => {
  const response = await apiClient.get(`/api/v1/services/${serviceId}/modifier-groups/admin`);
  return response.data;
};

// Creates a modifier group attached to the accommodation itself
// (`attach_to_variant_id` omitted/null) or to one of its live variants.
export const createAccommodationModifierGroup = async (
  accommodationId: number,
  data: ModifierGroupCreatePayload,
): Promise<ModifierGroupAdmin> => {
  const response = await apiClient.post(
    `/api/v1/accommodations/${accommodationId}/modifier-groups`,
    data,
  );
  return response.data;
};

// Edits a modifier group's name/description/selection bounds/display
// order/is_active. Attachment is fixed at create and cannot be re-sent
// here (ModifierGroupUpdatePayload carries no attach field).
export const updateAccommodationModifierGroup = async (
  accommodationId: number,
  groupId: number,
  data: ModifierGroupUpdatePayload,
): Promise<ModifierGroupAdmin> => {
  const response = await apiClient.put(
    `/api/v1/accommodations/${accommodationId}/modifier-groups/${groupId}`,
    data,
  );
  return response.data;
};

// Service-listing twin of createAccommodationModifierGroup.
export const createServiceModifierGroup = async (
  serviceId: number,
  data: ModifierGroupCreatePayload,
): Promise<ModifierGroupAdmin> => {
  const response = await apiClient.post(`/api/v1/services/${serviceId}/modifier-groups`, data);
  return response.data;
};

// Service-listing twin of updateAccommodationModifierGroup.
export const updateServiceModifierGroup = async (
  serviceId: number,
  groupId: number,
  data: ModifierGroupUpdatePayload,
): Promise<ModifierGroupAdmin> => {
  const response = await apiClient.put(
    `/api/v1/services/${serviceId}/modifier-groups/${groupId}`,
    data,
  );
  return response.data;
};

// Creates a modifier nested under an accommodation's modifier group. The
// server derives `code` as a slug of `name` — never sent by the caller.
export const createAccommodationModifier = async (
  accommodationId: number,
  groupId: number,
  data: ModifierCreatePayload,
): Promise<ModifierAdmin> => {
  const response = await apiClient.post(
    `/api/v1/accommodations/${accommodationId}/modifier-groups/${groupId}/modifiers`,
    data,
  );
  return response.data;
};

// Edits a modifier's fields (never `code`, immutable after create, and
// never `is_by_request`, authoring deferred). `is_active: false` soft-
// removes it; `is_active: true` reactivates it.
export const updateAccommodationModifier = async (
  accommodationId: number,
  groupId: number,
  modifierId: number,
  data: ModifierUpdatePayload,
): Promise<ModifierAdmin> => {
  const response = await apiClient.put(
    `/api/v1/accommodations/${accommodationId}/modifier-groups/${groupId}/modifiers/${modifierId}`,
    data,
  );
  return response.data;
};

// Service-listing twin of createAccommodationModifier.
export const createServiceModifier = async (
  serviceId: number,
  groupId: number,
  data: ModifierCreatePayload,
): Promise<ModifierAdmin> => {
  const response = await apiClient.post(
    `/api/v1/services/${serviceId}/modifier-groups/${groupId}/modifiers`,
    data,
  );
  return response.data;
};

// Service-listing twin of updateAccommodationModifier.
export const updateServiceModifier = async (
  serviceId: number,
  groupId: number,
  modifierId: number,
  data: ModifierUpdatePayload,
): Promise<ModifierAdmin> => {
  const response = await apiClient.put(
    `/api/v1/services/${serviceId}/modifier-groups/${groupId}/modifiers/${modifierId}`,
    data,
  );
  return response.data;
};

export interface AccommodationQuoteParams {
  start_date: string;
  end_date: string;
  guest_count: number;
  // Flat, already-expanded modifier-id list: one occurrence per selected
  // unit — a caller with SelectedModifier{modifier_id: 71, quantity: 2}
  // passes [71, 71] here (see `useQuote`, which owns that expansion).
  // Mirrors `selected_fee_codes`'s flat-array convention so this reuses
  // the same repeated-key helper with no modifier-specific branching.
  // Omitted when empty so the wire shape stays byte-identical for callers
  // that select nothing.
  selected_modifiers?: number[];
}

// Returns a server-computed PriceQuote for a native accommodation stay +
// modifier selection. Public and rate-limited, mirroring getRateBreakdown.
// Uses the native start_date/end_date vocabulary, distinct from NextPax
// rate-breakdown's check_in/check_out.
export const getAccommodationQuote = async (
  accommodationId: number,
  params: AccommodationQuoteParams,
): Promise<PriceQuote> => {
  const baseParams: Record<string, unknown> = {
    start_date: params.start_date,
    end_date: params.end_date,
    guest_count: params.guest_count,
  };
  const response = await apiClient.get(
    `/api/v1/accommodations/${accommodationId}/quote`,
    withRepeatedKeyArrayParam(baseParams, 'selected_modifiers', params.selected_modifiers),
  );
  return response.data;
};

export interface ServiceQuoteParams {
  guest_count: number;
  // The booked duration/count for a time-unit (hour/30min/day) service.
  // Ignored server-side for every other pricing unit. Omitted from the
  // request when absent so the wire shape stays byte-identical to today
  // for every non-time-unit service.
  estimated_unit_count?: number;
  // See AccommodationQuoteParams.selected_modifiers — same flat,
  // already-expanded convention.
  selected_modifiers?: number[];
}

// Returns a server-computed PriceQuote for a native service + modifier
// selection. No date params — a service quote is priced by guest_count
// alone, unlike the accommodation quote's stay window.
export const getServiceQuote = async (
  serviceId: number,
  params: ServiceQuoteParams,
): Promise<PriceQuote> => {
  const baseParams: Record<string, unknown> = {
    guest_count: params.guest_count,
    ...(params.estimated_unit_count !== undefined
      ? { estimated_unit_count: params.estimated_unit_count }
      : {}),
  };
  const response = await apiClient.get(
    `/api/v1/services/${serviceId}/quote`,
    withRepeatedKeyArrayParam(baseParams, 'selected_modifiers', params.selected_modifiers),
  );
  return response.data;
};

export interface EligibleRatePlansParams {
  check_in: string;
  check_out: string;
  /** Optional override for the eligibility "today" anchor. Backend
   *  defaults to `date.today()` when omitted. */
  today?: string;
}

export const getEligibleRatePlans = async (
  accommodationId: number,
  params: EligibleRatePlansParams,
): Promise<EligibleRatePlan[]> => {
  const response = await apiClient.get(
    `/api/v1/accommodations/${accommodationId}/eligible-rate-plans`,
    {
      params: {
        check_in: params.check_in,
        check_out: params.check_out,
        ...(params.today ? { today: params.today } : {}),
      },
    },
  );
  return response.data;
};

// Fetch the per-date availability calendar for a NextPax accommodation.
// `from` / `to` are inclusive-start / exclusive-end ISO date strings the
// backend route accepts as the `from` and `to` query params (the route
// aliases the date params to those short names). The wrapper exists so
// hooks and components stay off `axios` directly — the shared client
// handles JWT injection and 401/403 refresh.
export const getAvailabilityCalendar = async (
  accommodationId: number,
  from: string,
  to: string,
): Promise<AvailabilityCalendar> => {
  const response = await apiClient.get(
    `/api/v1/accommodations/${accommodationId}/availability-calendar`,
    {
      params: { from, to },
    },
  );
  return response.data;
};

// Returns the HTTP status code from an axios-thrown error, or null when
// the value isn't an axios error (e.g. a thrown string, runtime error,
// network failure with no response). Encapsulates the `axios.isAxiosError`
// type guard so callers don't need to import axios directly — the
// `no-restricted-imports` service-layer rule keeps direct axios imports
// out of components and pages.
export const httpStatusFromError = (err: unknown): number | null => {
  if (axios.isAxiosError(err)) {
    return err.response?.status ?? null;
  }
  return null;
};

// Returns the backend's `detail` message from an axios-thrown error, or
// null when the value isn't an axios error or carries no usable message:
// either a string `detail`, or a typed `{ code, message }` detail object's
// non-empty `message` (FastAPI's default validation-error shape is an
// array, which yields null). Same encapsulation rationale as `httpStatusFromError` above —
// keeps `axios.isAxiosError` and the response-body shape out of components.
export const errorDetailMessage = (err: unknown): string | null => {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (typeof detail === 'object' && detail !== null && !Array.isArray(detail)) {
      const { message } = detail as { message?: unknown };
      return typeof message === 'string' && message !== '' ? message : null;
    }
  }
  return null;
};

// Reads the listing count off a typed TAX_PROFILE_IN_USE 409 detail (see
// backend/schemas/tax_profile.py::TaxProfileInUseError), or null when the
// error isn't that shape at all (a different failure, or a plain-string
// detail). Kept beside errorDetailMessage/httpStatusFromError so
// TaxProfileManagement never sniffs the axios error shape itself.
export const taxProfileInUseListingCount = (err: unknown): number | null => {
  if (!axios.isAxiosError(err)) return null;
  const detail = err.response?.data?.detail;
  if (typeof detail !== 'object' || detail === null) return null;
  const { code, listing_count } = detail as { code?: unknown; listing_count?: unknown };
  if (code !== 'TAX_PROFILE_IN_USE' || typeof listing_count !== 'number') return null;
  return listing_count;
};

export const createAccommodation = async (
  data: AccommodationCreate,
): Promise<AccommodationAdmin> => {
  const response = await apiClient.post('/api/v1/accommodations/', data);
  return response.data;
};

// `data` accepts either a full parent update (AccommodationUpdatePayload,
// from buildAccommodationUpdatePayload) or a single child variant's
// rename/reprice body (AccommodationVariantCreate, from
// accommodationVariantToPayload) — both PUT the same route, and the
// backend's partial-update semantics (`.dict(exclude_unset=True)`) only
// touch the keys present on whichever shape was sent. Mirrors
// apiService.updateService's identical widened-union pattern.
export const updateAccommodation = async (
  id: number,
  data: AccommodationUpdatePayload | AccommodationVariantCreate,
): Promise<AccommodationAdmin> => {
  const response = await apiClient.put(`/api/v1/accommodations/${id}`, data);
  return response.data;
};

// Create a native-only child variant under a parent accommodation (mirrors
// createServiceVariant/createPackageVariant — the identical shape for
// accommodations). The parent id is a path param; `parent_id` on the
// created child is set server-side and never accepted here.
export const createAccommodationVariant = async (
  accommodationId: number,
  data: AccommodationVariantCreate,
): Promise<AccommodationAdmin> => {
  const response = await apiClient.post(`/api/v1/accommodations/${accommodationId}/variants`, data);
  return response.data;
};

// Unified Search Endpoint (returns both accommodations and services sorted together)
export const getUnifiedSearch = async (params?: {
  search?: string;
  sort_by?: string;
  order?: 'asc' | 'desc';
  check_in?: string;
  check_out?: string;
  limit?: number;
  offset?: number;
  category?: string;
}): Promise<
  Array<{
    type: 'accommodation' | 'service';
    data: Accommodation | Service;
  }>
> => {
  try {
    const response = await apiClient.get('/api/v1/search/all', { params });

    const results = response.data?.results;
    if (!results || !Array.isArray(results)) {
      return [];
    }

    return results.map(
      (result: { type: 'accommodation' | 'service'; data: Accommodation | Service }) =>
        result.type === 'service'
          ? { ...result, data: normaliseServicePriceFields(result.data as Service) }
          : result,
    );
  } catch (error) {
    console.error('Error fetching unified search:', error);
    return [];
  }
};

type ServiceListParams = {
  service_type?: string;
  location?: string | string[];
  max_capacity?: number;
  search?: string;
  sort_by?: string;
  order?: 'asc' | 'desc';
  shuffle_seed?: number;
  service_date?: string;
  category?: string;
  skip?: number;
  limit?: number;
};

// One request-and-normalise path for both service list callers; it throws on a failed request.
const requestServices = async (params?: ServiceListParams): Promise<Service[]> => {
  // Repeated `location` keys (the backend ORs them) need the bare repeated-key
  // form; a single string keeps the default wire shape.
  const response = await apiClient.get('/api/v1/services/', {
    params,
    ...(Array.isArray(params?.location) && {
      paramsSerializer: REPEATED_KEY_PARAMS_SERIALIZER,
    }),
  });

  if (!response.data || !Array.isArray(response.data)) {
    return [];
  }

  // Backend returns data directly, just ensure images are handled properly
  const processedData = response.data.map((item: any) => {
    // Sanitize available_days
    let availableDays: string[] = [];
    if (item.available_days) {
      if (typeof item.available_days === 'string') {
        // Check if it's a PostgreSQL array format
        if (item.available_days.startsWith('{') && item.available_days.endsWith('}')) {
          availableDays = parsePostgresArray(item.available_days);
        } else {
          availableDays = item.available_days.split(',').map((d: string) => d.trim());
        }
      } else if (Array.isArray(item.available_days)) {
        availableDays = item.available_days;
      }
    }

    return normaliseServicePriceFields({
      ...item,
      included: item.included ?? [],
      available_days: availableDays,
      images: item.images && item.images.length > 0 ? item.images : [],
      // option_count is populated by the unified search matview for parent
      // services and passed through via the spread above. Listed explicitly
      // here as a gap-driven extension so the reviewer can find it easily
      // (the Service type now includes option_count).
      option_count: item.option_count ?? null,
    });
  });

  return processedData;
};

// Service Endpoints
export const getServices = async (params?: ServiceListParams): Promise<Service[]> => {
  try {
    return await requestServices(params);
  } catch (error) {
    console.error('Error fetching services:', error);
    // Return empty array instead of mock data to prevent showing irrelevant results
    return [];
  }
};

/** Active service categories in admin order; a failed request rejects so callers can fall back. */
export const getServiceCategories = async (): Promise<PublicServiceCategory[]> => {
  const response = await apiClient.get<PublicServiceCategory[]>('/api/v1/service-categories/');
  return response.data;
};

/** Every category, active or not, in display order. */
export const listAdminServiceCategories = async (): Promise<AdminServiceCategory[]> => {
  const response = await apiClient.get<AdminServiceCategory[]>('/api/v1/admin/service-categories/');
  return response.data;
};

export const createServiceCategory = async (
  data: ServiceCategoryCreate,
): Promise<AdminServiceCategory> => {
  const response = await apiClient.post<AdminServiceCategory>(
    '/api/v1/admin/service-categories/',
    data,
  );
  return response.data;
};

export const updateServiceCategory = async (
  id: number,
  data: ServiceCategoryUpdate,
): Promise<AdminServiceCategory> => {
  const response = await apiClient.patch<AdminServiceCategory>(
    `/api/v1/admin/service-categories/${id}`,
    data,
  );
  return response.data;
};

/** Sends the full id list in the new order; resolves to the reordered list. */
export const reorderServiceCategories = async (ids: number[]): Promise<AdminServiceCategory[]> => {
  const response = await apiClient.put<AdminServiceCategory[]>(
    '/api/v1/admin/service-categories/order',
    { ids },
  );
  return response.data;
};

export const deleteServiceCategory = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/v1/admin/service-categories/${id}`);
};

/** The backend's explanation of a rejected (422) category request, or null for any other failure. */
export const serviceCategoryErrorMessage = (err: unknown): string | null =>
  axios.isAxiosError(err)
    ? unprocessableMessage(err.response?.status ?? null, err.response?.data?.detail)
    : null;

/** The typed 409 code on a failed category request, or null for any other failure. */
export const serviceCategoryConflictCode = (err: unknown): ServiceCategoryConflictCode | null =>
  axios.isAxiosError(err)
    ? conflictCodeFromResponse(err.response?.status ?? null, err.response?.data?.detail)
    : null;

/** The service list for a results page: a failed request rejects so the page can show an error. */
export const searchServices = (params: ServiceSearchParams): Promise<Service[]> =>
  requestServices(params);

// Package Endpoints
export const getPackages = async (): Promise<Package[]> => {
  const response = await apiClient.get('/api/v1/packages');
  return Array.isArray(response.data) ? response.data : [];
};

// Fetch a single active package by its slug. Mirrors `getAccommodation`'s
// error-propagation shape (throws untouched): the detail page needs to
// distinguish a 404 (unknown/inactive slug) from a transient failure so it can
// render a distinct not-found state.
export const getPackageBySlug = async (slug: string): Promise<Package> => {
  const response = await apiClient.get(`/api/v1/packages/${slug}`);
  return response.data;
};

// Fetch a single package by its numeric id, including an inactive one — the
// cart/checkout flow re-reads a package the guest already selected (which
// may since have been deactivated) rather than the public slug lookup,
// which is confined to active packages. Mirrors getPackageBySlug's
// error-propagation shape (throws untouched).
export const getPackageById = async (packageId: number): Promise<Package> => {
  const response = await apiClient.get(`/api/v1/packages/id/${packageId}`);
  return response.data;
};

// Package Admin Endpoints — every route below requires the admin guard
// server-side. Errors propagate to the caller (no swallow-to-empty here,
// unlike the public read above) so the admin screen can surface them.
export const getPackagesAdmin = async (): Promise<Package[]> => {
  const response = await apiClient.get('/api/v1/packages/admin');
  return response.data;
};

export const createPackage = async (data: PackageCreate): Promise<Package> => {
  const response = await apiClient.post('/api/v1/packages', data);
  return response.data;
};

export const updatePackage = async (packageId: number, data: PackageUpdate): Promise<Package> => {
  const response = await apiClient.put(`/api/v1/packages/${packageId}`, data);
  return response.data;
};

export const deletePackage = async (packageId: number): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/api/v1/packages/${packageId}`);
  return response.data;
};

// Create a child tier under a parent package (mirrors createServiceVariant —
// POST /api/v1/services/{id}/variants — the identical shape for packages).
// The parent id is a path param; `parent_id` on the created child is set
// server-side and never accepted here.
export const createPackageVariant = async (
  packageId: number,
  data: PackageVariantCreate,
): Promise<Package> => {
  const response = await apiClient.post(`/api/v1/packages/${packageId}/variants`, data);
  return response.data;
};

export const reorderPackages = async (orderedPackageIds: number[]): Promise<Package[]> => {
  const response = await apiClient.put('/api/v1/packages/reorder', {
    ordered_package_ids: orderedPackageIds,
  });
  return response.data;
};

// Return all live (non-soft-removed) child tiers of a parent package,
// ordered (display_order, id) — mirrors getServiceVariants/
// getAccommodationVariants for the package listing leg. Also doubles as the
// "Attach to" option source for ModifierGroupsEditor's package arm.
export const getPackageVariants = async (packageId: number): Promise<Package[]> => {
  const response = await apiClient.get(`/api/v1/packages/${packageId}/variants`);
  return response.data;
};

// Returns the effective modifier-group catalogue for a package (leaf union
// parent) — mirrors getAccommodationModifierGroups/getServiceModifierGroups
// for the package listing leg. Public and rate-limited.
export const getPackageModifierGroups = async (packageId: number): Promise<ModifierGroup[]> => {
  const response = await apiClient.get(`/api/v1/packages/${packageId}/modifier-groups`);
  return response.data;
};

// Admin authoring read twin of getAccommodationModifierGroupsAdmin/
// getServiceModifierGroupsAdmin for the package listing leg — every
// authored group across the package and its tiers, including inactive
// groups/modifiers.
export const getPackageModifierGroupsAdmin = async (
  packageId: number,
): Promise<ModifierGroupAdmin[]> => {
  const response = await apiClient.get(`/api/v1/packages/${packageId}/modifier-groups/admin`);
  return response.data;
};

// Package-listing twin of createAccommodationModifierGroup/
// createServiceModifierGroup.
export const createPackageModifierGroup = async (
  packageId: number,
  data: ModifierGroupCreatePayload,
): Promise<ModifierGroupAdmin> => {
  const response = await apiClient.post(`/api/v1/packages/${packageId}/modifier-groups`, data);
  return response.data;
};

// Package-listing twin of updateAccommodationModifierGroup/
// updateServiceModifierGroup.
export const updatePackageModifierGroup = async (
  packageId: number,
  groupId: number,
  data: ModifierGroupUpdatePayload,
): Promise<ModifierGroupAdmin> => {
  const response = await apiClient.put(
    `/api/v1/packages/${packageId}/modifier-groups/${groupId}`,
    data,
  );
  return response.data;
};

// Package-listing twin of createAccommodationModifier/createServiceModifier.
export const createPackageModifier = async (
  packageId: number,
  groupId: number,
  data: ModifierCreatePayload,
): Promise<ModifierAdmin> => {
  const response = await apiClient.post(
    `/api/v1/packages/${packageId}/modifier-groups/${groupId}/modifiers`,
    data,
  );
  return response.data;
};

// Package-listing twin of updateAccommodationModifier/updateServiceModifier.
export const updatePackageModifier = async (
  packageId: number,
  groupId: number,
  modifierId: number,
  data: ModifierUpdatePayload,
): Promise<ModifierAdmin> => {
  const response = await apiClient.put(
    `/api/v1/packages/${packageId}/modifier-groups/${groupId}/modifiers/${modifierId}`,
    data,
  );
  return response.data;
};

export interface PackageQuoteParams {
  guest_count: number;
  // See AccommodationQuoteParams.selected_modifiers / ServiceQuoteParams.
  // selected_modifiers — same flat, already-expanded, omit-when-empty
  // convention.
  selected_modifiers?: number[];
}

// Returns a server-computed PriceQuote for a package + modifier selection.
// Public and rate-limited, mirroring getServiceQuote's shape: no date
// params — a package's stay length is intrinsic (duration_nights), and its
// price is quoted/booked at unit_count=1 regardless.
export const getPackageQuote = async (
  packageId: number,
  params: PackageQuoteParams,
): Promise<PriceQuote> => {
  const baseParams: Record<string, unknown> = {
    guest_count: params.guest_count,
  };
  const response = await apiClient.get(
    `/api/v1/packages/${packageId}/quote`,
    withRepeatedKeyArrayParam(baseParams, 'selected_modifiers', params.selected_modifiers),
  );
  return response.data;
};

// Booking Request Endpoints

// Submit an authenticated customer's booking-intent request against a
// by-request accommodation, service, or package — the payload is
// polymorphic (mirrors backend/schemas/booking_request.py::
// BookingRequestCreate -> BookingRequestResponse; the caller sets exactly
// one of accommodation_id / service_id / package_id). A non-2xx response
// (401/404/422) rejects the promise untouched — the caller renders its own
// failure copy, matching submitEnquiry's error-propagation shape.
export const createBookingRequest = async (
  payload: BookingRequestCreate,
): Promise<BookingRequest> => {
  const response = await apiClient.post('/api/v1/booking-requests', payload);
  return response.data;
};

// The admin Requests-tab list — newest first, one polymorphic query on the
// backend (mirrors GET /admin/bookings' params shape).
export const getAdminBookingRequests = async (params?: {
  skip?: number;
  limit?: number;
  status_filter?: string;
}): Promise<BookingRequestAdmin[]> => {
  const response = await apiClient.get('/api/v1/admin/booking-requests', { params });
  return response.data;
};

// Issue, revise, or re-open a quote — the same call serves all three; the
// backend decides which based on the request's current status.
export const quoteBookingRequest = async (
  bookingRequestId: number,
  payload: BookingRequestQuoteRequest,
): Promise<BookingRequestAdmin> => {
  const response = await apiClient.post(
    `/api/v1/admin/booking-requests/${bookingRequestId}/quote`,
    payload,
  );
  return response.data;
};

export const declineBookingRequest = async (
  bookingRequestId: number,
): Promise<BookingRequestAdmin> => {
  const response = await apiClient.post(
    `/api/v1/admin/booking-requests/${bookingRequestId}/decline`,
  );
  return response.data;
};

// A single group, any owner — the admin surface carries no owner-gating
// (mirrors GET /api/v1/admin/quote-groups/{id}).
export const getAdminQuoteGroup = async (quoteGroupId: number): Promise<QuoteGroupAdmin> => {
  const response = await apiClient.get(`/api/v1/admin/quote-groups/${quoteGroupId}`);
  return response.data;
};

// Issue, revise, or re-open the single itemised quote covering every
// live member of the group — the same call serves all three; the
// backend decides which based on the group's current status (mirrors
// POST /api/v1/admin/quote-groups/{id}/quote).
export const quoteQuoteGroup = async (
  quoteGroupId: number,
  payload: QuoteGroupQuoteRequest,
): Promise<QuoteGroupAdmin> => {
  const response = await apiClient.post(
    `/api/v1/admin/quote-groups/${quoteGroupId}/quote`,
    payload,
  );
  return response.data;
};

// The caller's own booking requests, newest first (mirrors
// GET /api/v1/booking-requests).
export const getMyBookingRequests = async (): Promise<BookingRequest[]> => {
  const response = await apiClient.get('/api/v1/booking-requests');
  return response.data;
};

// A single owned booking request (mirrors GET /api/v1/booking-requests/{id}).
export const getBookingRequest = async (bookingRequestId: number): Promise<BookingRequest> => {
  const response = await apiClient.get(`/api/v1/booking-requests/${bookingRequestId}`);
  return response.data;
};

// Mint (or idempotently return) the on-session approval PaymentIntent for
// a QUOTED request the caller owns (mirrors
// POST /api/v1/booking-requests/{id}/approve).
export const approveBookingRequest = async (
  bookingRequestId: number,
): Promise<BookingRequestApproveResponse> => {
  const response = await apiClient.post(`/api/v1/booking-requests/${bookingRequestId}/approve`);
  return response.data;
};

// Idempotently finalize a booking-request approval payment after the
// frontend's Stripe Elements confirmPayment call (mirrors
// POST /api/v1/booking-requests/{id}/verify-payment) — the belt-and-
// suspenders finalizer alongside the Stripe webhook.
export const verifyBookingRequestPayment = async (
  bookingRequestId: number,
  paymentIntentId: string,
): Promise<BookingRequestVerifyPaymentResponse> => {
  const response = await apiClient.post(
    `/api/v1/booking-requests/${bookingRequestId}/verify-payment`,
    { payment_intent_id: paymentIntentId },
  );
  return response.data;
};

// Withdraw a REQUESTED/QUOTED request the caller owns (mirrors
// POST /api/v1/booking-requests/{id}/withdraw).
export const withdrawBookingRequest = async (bookingRequestId: number): Promise<BookingRequest> => {
  const response = await apiClient.post(`/api/v1/booking-requests/${bookingRequestId}/withdraw`);
  return response.data;
};

// Quote Group Endpoints

// The caller's own groups, newest first (mirrors
// GET /api/v1/booking-requests/groups).
export const getMyQuoteGroups = async (): Promise<QuoteGroup[]> => {
  const response = await apiClient.get('/api/v1/booking-requests/groups');
  return response.data;
};

// A single owned group (mirrors
// GET /api/v1/booking-requests/groups/{id}).
export const getQuoteGroup = async (quoteGroupId: number): Promise<QuoteGroup> => {
  const response = await apiClient.get(`/api/v1/booking-requests/groups/${quoteGroupId}`);
  return response.data;
};

// Mint (or idempotently return) the ONE on-session approval PaymentIntent
// for a QUOTED group the caller owns, covering the group's total_cents
// (mirrors POST /api/v1/booking-requests/groups/{id}/approve). Returns the
// same BookingRequestApproveResponse shape the single-request approve
// returns — one Stripe PaymentIntent shape serves both rails.
export const approveQuoteGroup = async (
  quoteGroupId: number,
): Promise<BookingRequestApproveResponse> => {
  const response = await apiClient.post(`/api/v1/booking-requests/groups/${quoteGroupId}/approve`);
  return response.data;
};

// Idempotently finalize a group approval payment after the frontend's
// Stripe Elements confirmPayment call (mirrors
// POST /api/v1/booking-requests/groups/{id}/verify-payment).
export const verifyQuoteGroupPayment = async (
  quoteGroupId: number,
  payload: { payment_intent_id: string },
): Promise<QuoteGroupVerifyPaymentResponse> => {
  const response = await apiClient.post(
    `/api/v1/booking-requests/groups/${quoteGroupId}/verify-payment`,
    payload,
  );
  return response.data;
};

// Withdraw a REQUESTED/QUOTED/EXPIRED group the caller owns (mirrors
// POST /api/v1/booking-requests/groups/{id}/withdraw).
export const withdrawQuoteGroup = async (quoteGroupId: number): Promise<QuoteGroup> => {
  const response = await apiClient.post(`/api/v1/booking-requests/groups/${quoteGroupId}/withdraw`);
  return response.data;
};

export const listBrands = async (): Promise<Brand[]> => {
  const response = await apiClient.get('/api/v1/admin/brands');
  return response.data;
};

export const createBrand = async (data: BrandCreate): Promise<Brand> => {
  const response = await apiClient.post('/api/v1/admin/brands', data);
  return response.data;
};

export const updateBrand = async (id: number, data: BrandUpdate): Promise<Brand> => {
  const response = await apiClient.put(`/api/v1/admin/brands/${id}`, data);
  return response.data;
};

export const getBrand = async (id: number): Promise<Brand> => {
  const response = await apiClient.get(`/api/v1/admin/brands/${id}`);
  return response.data;
};

/**
 * Fetch an accommodation's Brand's Google reviews (public, unauthenticated
 * GET, sibling to `getCompanyReviews` below). Always resolves with HTTP
 * 200 — no brand, no place id, or an upstream failure comes back as an
 * empty `reviews` array rather than a rejected promise, so callers only
 * need to branch on `reviews.length`.
 */
export const getBrandGoogleReviews = async (
  accommodationId: number,
): Promise<CompanyReviewsResponse> => {
  const response = await apiClient.get<CompanyReviewsResponse>(
    `/api/v1/accommodations/${accommodationId}/brand-google-reviews`,
  );
  return response.data;
};

export const getBrandSeasonCalendar = async (brandId: number): Promise<BrandSeasonCalendar> => {
  const response = await apiClient.get(`/api/v1/admin/brands/${brandId}/season-calendar`);
  return response.data;
};

export const putBrandSeasonCalendar = async (
  brandId: number,
  data: BrandSeasonCalendarWrite,
): Promise<BrandSeasonCalendar> => {
  const response = await apiClient.put(`/api/v1/admin/brands/${brandId}/season-calendar`, data);
  return response.data;
};

export const deleteBrandSeasonCalendar = async (brandId: number): Promise<void> => {
  await apiClient.delete(`/api/v1/admin/brands/${brandId}/season-calendar`);
};

export const getBrandStayFees = async (brandId: number): Promise<BrandStayFees> => {
  const response = await apiClient.get(`/api/v1/admin/brands/${brandId}/stay-fees`);
  return response.data;
};

export const putBrandStayFees = async (
  brandId: number,
  data: BrandStayFeesWrite,
): Promise<BrandStayFees> => {
  const response = await apiClient.put(`/api/v1/admin/brands/${brandId}/stay-fees`, data);
  return response.data;
};

export const listTaxProfiles = async (): Promise<TaxProfile[]> => {
  const response = await apiClient.get('/api/v1/admin/tax-profiles');
  return response.data;
};

export const createTaxProfile = async (data: TaxProfileWrite): Promise<TaxProfile> => {
  const response = await apiClient.post('/api/v1/admin/tax-profiles', data);
  return response.data;
};

export const updateTaxProfile = async (id: number, data: TaxProfileWrite): Promise<TaxProfile> => {
  const response = await apiClient.put(`/api/v1/admin/tax-profiles/${id}`, data);
  return response.data;
};

export const deleteTaxProfile = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/v1/admin/tax-profiles/${id}`);
};

// Helper function to parse PostgreSQL array format like '{item1,item2,item3}'
const parsePostgresArray = (value: string): string[] => {
  if (!value || value === '{}') return [];
  // Remove curly braces and split by comma
  const cleaned = value.replace(/^\{|\}$/g, '');
  if (!cleaned) return [];
  return cleaned.split(',').map((item) => item.trim());
};

// Coerce a single price field to a real number. The backend intermittently
// serialises `final_price_per_person` as a JSON string (e.g. '154.0') while
// `price_per_person` is a number, so both fields are treated the same way
// here: numbers pass through unchanged, null/undefined/empty become
// undefined (matching the optional `number` type declared in
// `types/index.ts`), and a non-numeric string is discarded rather than
// leaking `NaN` into arithmetic or the UI.
const coercePriceFieldToNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

// Normalise a service's price fields (and, recursively, each child variant's
// price fields — the option list renders `children` directly) so that
// `price_per_person` and `final_price_per_person` are always a real number
// or undefined by the time the data reaches components. This is the
// boundary at which the backend's string-serialised
// `final_price_per_person` gets converted before it can be rendered raw or
// relied on for arithmetic.
export const normaliseServicePriceFields = <
  T extends {
    price_per_person?: unknown;
    final_price_per_person?: unknown;
    children?: unknown;
  },
>(
  service: T,
): T => {
  const normalisedChildren = Array.isArray(service.children)
    ? service.children.map((child) =>
        normaliseServicePriceFields(
          child as { price_per_person?: unknown; final_price_per_person?: unknown },
        ),
      )
    : service.children;

  return {
    ...service,
    price_per_person: coercePriceFieldToNumber(service.price_per_person),
    final_price_per_person: coercePriceFieldToNumber(service.final_price_per_person),
    children: normalisedChildren,
  };
};

const fetchService = async (path: string): Promise<Service> => {
  try {
    const response = await apiClient.get(path);
    const item = response.data;

    // Sanitize available_days - handle array, object, or null/undefined
    let availableDays: string[] = [];
    if (item.available_days) {
      if (typeof item.available_days === 'string') {
        // Check if it's a PostgreSQL array format
        if (item.available_days.startsWith('{') && item.available_days.endsWith('}')) {
          availableDays = parsePostgresArray(item.available_days);
        } else {
          availableDays = item.available_days.split(',').map((d: string) => d.trim());
        }
      } else if (Array.isArray(item.available_days)) {
        availableDays = item.available_days;
      }
      // If it's an object (like {}), just use empty array
    }

    // Null is the backend's valid unset state; the view model uses arrays.
    const sanitisedChildren: ServiceChildSummary[] = Array.isArray(item.children)
      ? item.children.map((child: ServiceChildSummary) => ({
          ...child,
          included: child.included ?? [],
        }))
      : [];

    // Backend returns data directly, just ensure images are handled properly
    return normaliseServicePriceFields({
      ...item,
      included: item.included ?? [],
      available_days: availableDays,
      images: item.images && item.images.length > 0 ? item.images : [],
      children: sanitisedChildren,
    });
  } catch (error: any) {
    console.error('Error in getService:', error);
    // If it's a 422 validation error, the backend has corrupted data
    if (error.response?.status === 422) {
      const validationError = new Error('Service has invalid data in database');
      (validationError as any).response = error.response;
      (validationError as any).isValidationError = true;
      throw validationError;
    }
    throw error;
  }
};

export const getService = (id: number): Promise<Service> => fetchService(`/api/v1/services/${id}`);

export const getServiceBySlug = (slug: string): Promise<Service> =>
  fetchService(`/api/v1/services/by-slug/${encodeURIComponent(slug)}`);

export const createService = async (data: ServiceCreate): Promise<Service> => {
  const response = await apiClient.post('/api/v1/services/', data);
  return response.data;
};

// Parent edits, option edits and inclusion-only curation share the partial
// PUT route. Omitted fields retain their existing values on the server.
export const updateService = async (
  id: number,
  data: ServiceUpdatePayload | ServiceVariantCreate | Pick<ServiceUpdatePayload, 'included'>,
): Promise<Service> => {
  const response = await apiClient.put(`/api/v1/services/${id}`, data);
  return response.data;
};

export const getServiceVariants = async (serviceId: number): Promise<Service[]> => {
  const response = await apiClient.get(`/api/v1/services/${serviceId}/variants`);
  return response.data.map((variant: Service) =>
    normaliseServicePriceFields({
      ...variant,
      included: variant.included ?? [],
    }),
  );
};

export const createServiceVariant = async (
  serviceId: number,
  data: ServiceVariantCreate,
): Promise<Service> => {
  const response = await apiClient.post(`/api/v1/services/${serviceId}/variants`, data);
  return response.data;
};

// Advanced Search Endpoints
export const getSearchFilters = async (): Promise<FilterOptions> => {
  const response = await apiClient.get('/api/v1/search/accommodations/filters');
  return response.data;
};

/**
 * Typeahead suggestions for the Discover hero "Where" search. Empty `q`
 * returns the top-inventory default set; a non-empty `q` returns prefix-first
 * matches. Degrades gracefully — this backs a live-as-you-type popover, so a
 * transient failure must return an empty list rather than throw and break
 * the input.
 */
export const getLocationSuggestions = async (
  q?: string,
  limit?: number,
  kind?: LocationSuggestionKind,
): Promise<LocationSuggestion[]> => {
  try {
    const response = await apiClient.get('/api/v1/search/locations', {
      params: { q, limit, kind },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching location suggestions:', error);
    return [];
  }
};

// Booking Endpoints
export const getBookings = async (): Promise<Booking[]> => {
  const response = await apiClient.get('/api/v1/bookings/');
  return response.data;
};

export const getBooking = async (bookingId: number): Promise<Booking> => {
  const response = await apiClient.get(`/api/v1/bookings/${bookingId}`);
  return response.data;
};

// NextPax draft → confirm flow. The draft row is the cross-table linkage
// point between the Stripe PaymentIntent (authorised next via
// `createPaymentIntentForDraft`) and the NextPax booking that confirms
// it. See backend/api/v1/bookings.py + ADR-0001.
export const createNextPaxBookingDraft = async (
  data: NextPaxBookingDraftCreate,
): Promise<NextPaxBookingDraftResponse> => {
  const response = await apiClient.post('/api/v1/bookings/draft-nextpax', data);
  return response.data;
};

export const createPaymentIntentForDraft = async (data: {
  draft_id: number;
}): Promise<PaymentIntentResponse> => {
  const response = await apiClient.post('/api/v1/payments/create-intent', data);
  return response.data;
};

// Read-only snapshot of the owning user's draft state, used by the
// NextPax payment page to recover from a mid-flow page refresh. The
// backend collapses "missing" and "owned by another user" into a
// single 404 so the response never surfaces draft existence to a
// caller who cannot use it. See backend/api/v1/bookings.py
// `get_nextpax_booking_draft_state`.
export const getNextPaxBookingDraftState = async (
  draftId: number,
): Promise<BookingDraftStateResponse> => {
  const response = await apiClient.get(`/api/v1/bookings/draft-nextpax/${draftId}`);
  return response.data;
};

// Guest-fields-only update for an existing NextPax BookingDraft — the write
// counterpart to `getNextPaxBookingDraftState`'s read. Lets a guest correct
// their mainBooker details after a card has already been entered without
// recreating the draft row, so `draft_id` and the already-authorised Stripe
// PaymentIntent never change. See backend/api/v1/bookings.py's
// `PATCH /draft-nextpax/{draft_id}` route.
export const updateNextPaxDraftGuestFields = async (
  draftId: number,
  data: NextPaxDraftGuestFieldsUpdate,
): Promise<NextPaxBookingDraftResponse> => {
  const response = await apiClient.patch(`/api/v1/bookings/draft-nextpax/${draftId}`, data);
  return response.data;
};

// Promote a NextPax `BookingDraft` into a confirmed booking by handing
// the authorised Stripe payment intent to the backend. The backend in
// turn invokes `NextPaxProvider.create_booking_from_draft` and surfaces
// the confirmed `Booking` row inside an `AccommodationBookingResponse`
// envelope (booking + message + optional warning). The caller navigates
// to /booking-confirmation/{response.booking.id}.
export const confirmNextPaxBooking = async (
  data: NextPaxBookingConfirmRequest,
): Promise<AccommodationBookingResponse> => {
  const response = await apiClient.post('/api/v1/bookings/confirm-nextpax', data);
  return response.data;
};

// Concierge Services Endpoints
export const getConciergeServices = async (): Promise<any[]> => {
  const response = await apiClient.get('/api/v1/concierge');
  return response.data;
};

export const getBookingConciergeServices = async (bookingId: number): Promise<any[]> => {
  const response = await apiClient.get(`/api/v1/concierge/booking/${bookingId}`);
  return response.data;
};

export const createConciergeService = async (serviceData: any): Promise<any> => {
  const response = await apiClient.post('/api/v1/concierge', serviceData);
  return response.data;
};

/**
 * Response payload from `POST /api/v1/bookings/{id}/cancel`. Mirrors
 * `backend/schemas/booking.py::BookingCancellationResponse`. Used by the
 * customer-facing cancel flow (`useCancelBooking` + `BookingDetailPage`)
 * to render the post-cancel hero state with the refund line.
 *
 * `refund_destination_last4` / `refund_destination_brand` carry the
 * Stripe charge-destination summary so the cancelled hero state can
 * say "Refund issued to Visa ending in 4242." Both are nullable —
 * the backend leaves them unset when the Stripe metadata fetch fails
 * or the payment method exposes no card details, in which case the
 * UI falls back to the generic "Refund to original payment method"
 * line.
 */
export interface BookingCancellationResult {
  message: string;
  refund_issued: boolean;
  refund_failed: boolean;
  refund_amount_cents: number;
  refund_destination_last4: string | null;
  refund_destination_brand: string | null;
  /**
   * True when the backend has queued the refund decision for admin
   * review rather than auto-issuing a Stripe refund. Mirrors
   * `backend/schemas/booking.py::BookingCancellationResponse.refund_pending`.
   * The frontend renders "Our team is reviewing your refund" copy
   * (no dollar amount, no card destination) when this is true.
   */
  refund_pending: boolean;
}

/**
 * Backend wire shape for HTTP 422 cancellation failures. Mirrors
 * `backend/schemas/booking.py::CancellationError`, which the route
 * layer serialises and passes as the `detail` of an
 * `HTTPException(status_code=422, ...)`. FastAPI surfaces it on the
 * wire as `{"detail": {"code": "...", "message": "..."}}`; the
 * apiService categoriser keys on `code` rather than substring-matching
 * `message` text so a copy edit on the backend can't silently drop
 * customers into the UNKNOWN modal copy.
 */
export interface CancellationErrorDetail {
  code: string;
  message: string;
}

/**
 * Typed error categories surfaced by `cancelBooking`. The service-layer
 * boundary is responsible for inspecting axios error shapes (status +
 * body detail) once; downstream consumers (the `useCancelBooking` hook
 * and `CancelBookingModal`) switch on the enum to render per-reason
 * inline copy without re-parsing strings.
 *
 * - `NO_POLICY_CONFIGURED` — 422 with backend code
 *   `NO_POLICY_CONFIGURED`. The accommodation has no cancellation
 *   policy data attached; the customer can't self-cancel and needs to
 *   contact support. Distinct from `POLICY_REJECTED` because the
 *   "past the cancellation window" copy is a lie when there's no
 *   policy to enforce.
 * - `POLICY_REJECTED` — 422 with backend code `POLICY_DENIES` (a real
 *   policy-based denial — date-based cutoff, non-refundable rate plan,
 *   etc.). The backend short-circuits before touching the supplier.
 * - `SUPPLIER_REJECTED` — 422 with backend code `SUPPLIER_REJECTED` or
 *   `MISSING_SUPPLIER_BOOKING_ID`. The supplier-side cancel call
 *   failed or could not be reached. Includes the
 *   `~/shared/docs/integrations/nextpax/gotchas.md` "all-null-data"
 *   envelope class once it surfaces on the cancel path.
 * - `REFUND_FAILED` — 200 OK with body `refund_failed: true`, or 422
 *   with backend code `REFUND_FAILED`. The supplier cancel succeeded
 *   but Stripe didn't issue the refund automatically, so the customer
 *   needs the "support will reach out" copy rather than the
 *   supplier-reference copy.
 * - `UNKNOWN` — every other thrown error (network failure, 500 with an
 *   unrecognised detail, 422 with `BOOKING_NOT_FOUND` /
 *   `ACCOMMODATION_NOT_FOUND` / `UNKNOWN`, non-axios runtime error).
 *   Falls back to the generic error surface.
 */
export enum CancelBookingErrorCategory {
  NO_POLICY_CONFIGURED = 'NO_POLICY_CONFIGURED',
  POLICY_REJECTED = 'POLICY_REJECTED',
  SUPPLIER_REJECTED = 'SUPPLIER_REJECTED',
  REFUND_FAILED = 'REFUND_FAILED',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Typed error thrown by `cancelBooking` so the network seam can hand
 * the modal layer a category enum instead of a raw axios error. The
 * `category` field is the load-bearing surface; `message` carries the
 * backend's original detail for logging only.
 */
export class BookingCancellationError extends Error {
  readonly category: CancelBookingErrorCategory;

  constructor(category: CancelBookingErrorCategory, message: string) {
    super(message);
    this.name = 'BookingCancellationError';
    this.category = category;
  }
}

const _ALREADY_CANCELLED_SUBSTRING = 'already cancelled';
const _FALLBACK_CANCEL_FAILURE_MESSAGE = 'Cancellation failed';

/**
 * Mapping from backend `BookingCancellationErrorCode` enum values
 * (mirrored verbatim from `backend/schemas/booking.py`) to the
 * frontend `CancelBookingErrorCategory`. Adding a member to the
 * backend enum requires a corresponding entry here; the categoriser
 * falls back to UNKNOWN for any unrecognised code so the modal still
 * surfaces a generic error rather than crashing on the missing arm.
 *
 * - `NO_POLICY_CONFIGURED` is a distinct category (not folded into
 *   POLICY_REJECTED) because the customer copy differs: no policy
 *   data means "contact support", a true policy denial means "past
 *   the cancellation window".
 * - `MISSING_SUPPLIER_BOOKING_ID` collapses onto SUPPLIER_REJECTED —
 *   from the customer's perspective both mean "we can't reach the
 *   supplier to cancel".
 * - `BOOKING_NOT_FOUND` / `ACCOMMODATION_NOT_FOUND` collapse onto
 *   UNKNOWN because these are 404-shaped failures dressed as 422 in
 *   the typed-detail flow; the modal's generic copy is appropriate.
 */
const _BACKEND_CODE_TO_FRONTEND_CATEGORY: Record<string, CancelBookingErrorCategory> = {
  NO_POLICY_CONFIGURED: CancelBookingErrorCategory.NO_POLICY_CONFIGURED,
  POLICY_DENIES: CancelBookingErrorCategory.POLICY_REJECTED,
  SUPPLIER_REJECTED: CancelBookingErrorCategory.SUPPLIER_REJECTED,
  MISSING_SUPPLIER_BOOKING_ID: CancelBookingErrorCategory.SUPPLIER_REJECTED,
  REFUND_FAILED: CancelBookingErrorCategory.REFUND_FAILED,
  BOOKING_NOT_FOUND: CancelBookingErrorCategory.UNKNOWN,
  ACCOMMODATION_NOT_FOUND: CancelBookingErrorCategory.UNKNOWN,
  UNKNOWN: CancelBookingErrorCategory.UNKNOWN,
};

/**
 * Narrow the axios error body's `detail` field to the typed
 * `CancellationErrorDetail` shape the backend now emits (per
 * `backend/services/booking_invariants.py::raise_if_cancellation_failed`).
 * Returns `null` when the body doesn't match — the caller falls back
 * to string-detail extraction for the idempotency-substring path or
 * to UNKNOWN.
 */
const _extractTypedCancellationErrorDetail = (caught: unknown): CancellationErrorDetail | null => {
  if (!axios.isAxiosError(caught)) return null;
  const responseData = caught.response?.data;
  if (typeof responseData !== 'object' || responseData === null) return null;
  if (!('detail' in responseData)) return null;
  const detail = (responseData as { detail: unknown }).detail;
  if (typeof detail !== 'object' || detail === null) return null;
  const detailRecord = detail as Record<string, unknown>;
  const code = detailRecord.code;
  const message = detailRecord.message;
  if (typeof code !== 'string' || typeof message !== 'string') return null;
  return { code, message };
};

/**
 * Extract a human-readable message from a thrown error body's `detail`
 * field, whether `detail` is a plain string or the typed `{code,
 * message}` object shape (see e.g.
 * `backend/schemas/stay_fee.py::StayFeeValidationError`,
 * `backend/schemas/tax_profile.py::TaxProfileValidationError`) — `null`
 * when the shape matches neither. A superset of `errorDetailMessage`
 * above (which only reads the string shape); admin editors whose
 * backend can return either shape on the same route call this instead.
 * Also used internally for the cancellation seam's idempotency substring
 * check (backend legacy 422 with `detail: "Booking is already
 * cancelled"`) — `_categoriseCancelError` still prefers the typed-code
 * path for every other cancellation failure mode.
 *
 * Duck-types the `{response: {data: {detail}}}` shape structurally
 * rather than gating on `axios.isAxiosError` -- every real axios error
 * this seam receives has this shape (so behaviour is unchanged for
 * production callers), and structural duck-typing is what let this
 * logic previously live as a private, axios-import-free helper inside
 * `SeasonCalendarEditor.tsx` (the service-layer boundary rule keeps
 * direct axios imports out of components); lifting it here to a shared,
 * exported helper keeps that same tolerance rather than narrowing it to
 * only real `AxiosError` instances.
 */
export const errorMessageFromDetail = (caught: unknown): string | null => {
  if (typeof caught !== 'object' || caught === null || !('response' in caught)) return null;
  const response = (caught as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null || !('data' in response)) return null;
  const responseData = (response as { data?: unknown }).data;
  if (typeof responseData !== 'object' || responseData === null || !('detail' in responseData)) {
    return null;
  }
  const detail = (responseData as { detail: unknown }).detail;
  if (typeof detail === 'string') return detail;
  // The typed-detail shape carries the human-readable text on
  // `detail.message` — surface that to the idempotency check too so a
  // backend that switches an idempotency 422 to the typed shape
  // continues to route the user to the synthetic-success path.
  if (typeof detail === 'object' && detail !== null) {
    const message = (detail as Record<string, unknown>).message;
    if (typeof message === 'string') return message;
  }
  return null;
};

/**
 * Categorise a thrown axios error from `POST /api/v1/bookings/{id}/cancel`
 * into the `CancelBookingErrorCategory` enum. Prefers the typed
 * `detail.code` shape the backend now emits; falls back to the
 * string-detail path only when the body doesn't match the typed shape
 * (defensive — handles malformed responses or a backend regression).
 * Returns `null` when the caught value isn't a known cancellation
 * failure shape (caller falls back to UNKNOWN by re-throwing).
 */
const _categoriseCancelError = (caught: unknown): CancelBookingErrorCategory | null => {
  if (!axios.isAxiosError(caught)) return null;
  const status = caught.response?.status;
  if (status !== 422) return null;
  const typedDetail = _extractTypedCancellationErrorDetail(caught);
  if (typedDetail !== null) {
    return (
      _BACKEND_CODE_TO_FRONTEND_CATEGORY[typedDetail.code] ?? CancelBookingErrorCategory.UNKNOWN
    );
  }
  return null;
};

/**
 * `true` when the caught error carries the "already cancelled" detail
 * — the native provider returns this 422 when the user re-clicks
 * Confirm in a stale tab. The hook re-routes this case to a synthetic
 * idempotent success so the modal closes and the detail page transitions
 * to the cancelled hero state instead of surfacing a misleading error.
 */
const _isAlreadyCancelledError = (caught: unknown): boolean => {
  const detail = errorMessageFromDetail(caught);
  return detail !== null && detail.toLowerCase().includes(_ALREADY_CANCELLED_SUBSTRING);
};

const _ALREADY_CANCELLED_SYNTHETIC_PAYLOAD: BookingCancellationResult = {
  message: 'Booking already cancelled',
  refund_issued: false,
  refund_failed: false,
  refund_amount_cents: 0,
  refund_destination_last4: null,
  refund_destination_brand: null,
  refund_pending: false,
};

export const cancelBooking = async (
  bookingId: number,
  reason?: string,
): Promise<BookingCancellationResult> => {
  let response;
  try {
    response = await apiClient.post(`/api/v1/bookings/${bookingId}/cancel`, { reason });
  } catch (caught) {
    if (_isAlreadyCancelledError(caught)) {
      return _ALREADY_CANCELLED_SYNTHETIC_PAYLOAD;
    }
    const category = _categoriseCancelError(caught);
    if (category !== null) {
      const detailMessage = errorMessageFromDetail(caught);
      const fallbackMessage =
        caught instanceof Error ? caught.message : _FALLBACK_CANCEL_FAILURE_MESSAGE;
      throw new BookingCancellationError(category, detailMessage ?? fallbackMessage);
    }
    throw caught;
  }
  const payload = response.data as BookingCancellationResult;
  // Cancel succeeded but Stripe refund didn't fire automatically — the
  // customer needs the "support will reach out" copy rather than the
  // post-cancel hero state, so flip this into a thrown typed error at
  // the network boundary.
  if (payload.refund_failed === true) {
    throw new BookingCancellationError(CancelBookingErrorCategory.REFUND_FAILED, 'Refund failed');
  }
  return payload;
};

/**
 * Response payload from `POST /api/v1/bookings/{id}/request-cancellation`.
 * Mirrors `backend/schemas/booking.py::BookingCancellationRequestResponse`.
 * Used by the admin-mediated native cancellation flow — the customer
 * submits a *request* that flips the booking to
 * `PENDING_CANCELLATION_REVIEW`; an admin reviews and the existing admin
 * cancel route performs the supplier-side cancel and Stripe refund. The
 * `requested_at` field is an ISO 8601 timestamp captured server-side at
 * the transition point, surfaced on the customer-facing pending banner.
 */
export interface BookingCancellationRequestResponse {
  message: string;
  booking_id: number;
  requested_at: string;
}

/**
 * Submit a customer-initiated *request* to cancel a NATIVE booking.
 * Posts `{ reason }` to `/api/v1/bookings/{id}/request-cancellation` and
 * returns the typed response. The endpoint does not issue any provider
 * call or Stripe refund — it flips the booking to
 * `PENDING_CANCELLATION_REVIEW`, records the reason, and queues an admin
 * notification. NextPax bookings continue to use `cancelBooking` (the
 * existing supplier-mediated `/cancel` route).
 *
 * Errors are bubbled up untouched — the request flow's modal surfaces a
 * generic "Request failed. Please try again." copy on rejection rather
 * than a per-reason categorisation, so no typed error class wraps the
 * thrown axios error.
 */
export const requestCancellation = async (
  bookingId: number,
  reason?: string,
): Promise<BookingCancellationRequestResponse> => {
  const response = await apiClient.post(`/api/v1/bookings/${bookingId}/request-cancellation`, {
    reason,
  });
  return response.data;
};

export const modifyBooking = async (
  bookingId: number,
  updates: { guest_count?: number; special_requests?: string },
): Promise<Booking> => {
  const response = await apiClient.put(`/api/v1/bookings/${bookingId}`, updates);
  return response.data;
};

/**
 * Idempotently verify a Stripe Checkout Session completion against the
 * backend so an AWAITING_PAYMENT booking flips to CONFIRMED. Called on
 * mount of the `/booking/:id/success` page with the `session_id` Stripe
 * passes back via the `success_url` query param. The backend webhook is
 * unreachable from Stripe in local dev (`stripe listen --forward-to`
 * required), so this verify endpoint is the belt-and-suspenders flip
 * path that works regardless of webhook delivery.
 *
 * Wire shape pinned by `apiService.test.ts::verifyCheckoutSession`:
 * `{ session_id }` (snake_case to mirror the backend Pydantic schema).
 */
export const verifyCheckoutSession = async (
  bookingId: number,
  sessionId: string,
): Promise<CheckoutSessionVerifyResponse> => {
  const response = await apiClient.post(`/api/v1/bookings/${bookingId}/verify-checkout-session`, {
    session_id: sessionId,
  });
  return response.data;
};

// Review System Endpoints
export const getAccommodationReviews = async (
  accommodationId: number,
  skip?: number,
  limit?: number,
  statusFilter?: string,
): Promise<Review[]> => {
  const response = await apiClient.get(`/api/v1/reviews/accommodation/${accommodationId}`, {
    params: { skip, limit, status_filter: statusFilter },
  });
  return response.data;
};

export const createReview = async (reviewData: ReviewCreate): Promise<Review> => {
  const response = await apiClient.post('/api/v1/reviews/', reviewData);
  return response.data;
};

export const getExternalReviews = async (
  accommodationId: number,
  skip?: number,
  limit?: number,
): Promise<ExternalReview[]> => {
  const response = await apiClient.get(
    `/api/v1/reviews/accommodation/${accommodationId}/external`,
    { params: { skip, limit } },
  );
  return response.data;
};

export const getAccommodationReviewStats = async (
  accommodationId: number,
): Promise<ReviewStats> => {
  const response = await apiClient.get(`/api/v1/reviews/accommodation/${accommodationId}/stats`);
  return response.data;
};

export const getUserReviews = async (): Promise<Review[]> => {
  const response = await apiClient.get('/api/v1/reviews/user/my-reviews');
  return response.data;
};

export const updateReview = async (reviewId: number, updates: Partial<Review>): Promise<Review> => {
  const response = await apiClient.put(`/api/v1/reviews/${reviewId}`, updates);
  return response.data;
};

export const deleteReview = async (reviewId: number): Promise<void> => {
  await apiClient.delete(`/api/v1/reviews/${reviewId}`);
};

// Wishlist Endpoints
export const getWishlist = async (): Promise<WishlistItem[]> => {
  const response = await apiClient.get('/api/v1/wishlist/');
  return response.data;
};

export const addToWishlist = async (accommodationId: number): Promise<void> => {
  await apiClient.post('/api/v1/wishlist/', { accommodation_id: accommodationId });
};

export const removeFromWishlist = async (accommodationId: number): Promise<void> => {
  await apiClient.delete(`/api/v1/wishlist/${accommodationId}`);
};

// Image Management Endpoints

// Uploads a single file to the generic S3-backed image endpoint and
// returns its stored URL. Unlike `uploadPropertyImages` below (which posts
// a `files` field for a multi-file, listing-scoped upload), this posts the
// single `file` field the route actually declares — for callers, like
// package image upload and the admin accommodation-create image uploader,
// that upload one file at a time.
//
// `scope`, when supplied, threads `accommodation_id`/`package_id`/`service_id`/
// `brand_id`/`blog_post_id` as query params so the backend scopes the upload into that
// entity's own S3 folder (`property-images/{accommodationId}/…`,
// `package-images/{packageId}/…`, `service-images/{serviceId}/…`, or
// `brand-images/{brandId}/…`, or `general-images/blog-{blogPostId}/…`) instead of the generic dump. Omitting it
// preserves the pre-existing unscoped-upload behaviour.
export const uploadImage = async (
  file: File,
  scope?: {
    accommodationId?: number;
    packageId?: number;
    serviceId?: number;
    brandId?: number;
    blogPostId?: number;
  },
): Promise<ImageUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const params: Record<string, number> = {};
  if (scope?.accommodationId !== undefined) {
    params.accommodation_id = scope.accommodationId;
  }
  if (scope?.packageId !== undefined) {
    params.package_id = scope.packageId;
  }
  if (scope?.serviceId !== undefined) {
    params.service_id = scope.serviceId;
  }
  if (scope?.brandId !== undefined) {
    params.brand_id = scope.brandId;
  }
  if (scope?.blogPostId !== undefined) {
    params.blog_post_id = scope.blogPostId;
  }

  const response = await apiClient.post('/api/v1/images/upload', formData, {
    params: Object.keys(params).length > 0 ? params : undefined,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadPropertyImages = async (
  files: FileList,
  accommodationId?: number,
): Promise<ImageUploadResponse[]> => {
  const formData = new FormData();
  Array.from(files).forEach((file) => formData.append('files', file));
  if (accommodationId) {
    formData.append('accommodation_id', accommodationId.toString());
  }

  const response = await apiClient.post('/api/v1/images/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deletePropertyImage = async (imageId: number): Promise<void> => {
  await apiClient.delete(`/api/v1/images/${imageId}`);
};

// Replaces all of an accommodation's images with the given URL list
// (mirrors backend api/v1/accommodations.py::update_accommodation_images —
// note this hits the accommodations router, not the images router used by
// the other helpers in this section). The images themselves are uploaded
// beforehand, one at a time, via `uploadImage(file, { accommodationId })`;
// this call only attaches the resulting URLs to the accommodation record.
export const setAccommodationImages = async (
  accommodationId: number,
  imageUrls: string[],
): Promise<AccommodationAdmin> => {
  const response = await apiClient.put(`/api/v1/accommodations/${accommodationId}/images`, {
    images: imageUrls,
  });
  return response.data;
};

// One row of the replace-all bedroom write payload (mirrors backend
// schemas/bedroom.py::BedroomWrite). `property_image_id` names a photo
// from the unit's own gallery, or its parent's when the unit is an
// option (ADR-0083) -- omitted/null leaves the row unlinked.
export interface BedroomWriteRow {
  name: string;
  beds: BedLine[];
  property_image_id?: number | null;
}

// Replace-all write for a bookable unit's bedroom rows (mirrors backend
// api/v1/accommodations.py::update_accommodation_bedrooms). Admin-only,
// native-only; a container parent with live options refuses a non-empty
// payload. Used by the admin bedroom editor.
export const setAccommodationBedrooms = async (
  accommodationId: number,
  rows: BedroomWriteRow[],
): Promise<BedroomDetail[]> => {
  const response = await apiClient.put(`/api/v1/accommodations/${accommodationId}/bedrooms`, {
    bedrooms: rows,
  });
  return response.data;
};

// Admin read of an accommodation's exact point (mirrors backend
// api/v1/accommodations.py::get_accommodation_location).
export const getAccommodationLocation = async (
  accommodationId: number,
): Promise<AccommodationLocation> => {
  const response = await apiClient.get(`/api/v1/accommodations/${accommodationId}/location`);
  return response.data;
};

// Admin write of an accommodation's exact point; both values or both null
// (null clears it). Native-only (mirrors update_accommodation_location).
export const updateAccommodationLocation = async (
  accommodationId: number,
  payload: AccommodationLocation,
): Promise<AccommodationLocation> => {
  const response = await apiClient.put(
    `/api/v1/accommodations/${accommodationId}/location`,
    payload,
  );
  return response.data;
};

// Replaces all of a service's images with the given URL list (mirrors backend
// api/v1/services.py::update_service_images — the bulk PUT counterpart of
// setAccommodationImages). The images themselves are uploaded beforehand, one
// at a time, via `uploadImage(file, { serviceId })`; this call only attaches
// the resulting URLs to the service record.
export const setServiceImages = async (
  serviceId: number,
  imageUrls: string[],
): Promise<Service> => {
  const response = await apiClient.put(`/api/v1/services/${serviceId}/images`, {
    images: imageUrls,
  });
  return response.data;
};

// Replaces all of a package's images with the given URL list (mirrors backend
// api/v1/packages.py::update_package_images — the bulk PUT counterpart of
// setAccommodationImages/setServiceImages). The images themselves are
// uploaded beforehand, one at a time, via `uploadImage(file, { packageId })`;
// this call only attaches the resulting URLs to the package record.
export const setPackageImages = async (
  packageId: number,
  imageUrls: string[],
): Promise<Package> => {
  const response = await apiClient.put(`/api/v1/packages/${packageId}/images`, {
    images: imageUrls,
  });
  return response.data;
};

// Admin Endpoints
export const getAdminUserPage = async (params: AdminUserListQuery): Promise<AdminUserPage> => {
  const response = await apiClient.get('/api/v1/admin/users', { params });
  return response.data;
};

export const downloadMarketingContactsCsv = async (): Promise<Blob> => {
  const response = await apiClient.get('/api/v1/admin/users/export.csv', {
    responseType: 'blob',
  });
  return response.data;
};

export const updateUser = async (userId: number, updates: UserUpdate): Promise<UserWithStats> => {
  const response = await apiClient.put(`/api/v1/admin/users/${userId}`, updates);
  return response.data;
};

export const deactivateUser = async (userId: number): Promise<void> => {
  await apiClient.delete(`/api/v1/admin/users/${userId}`);
};

export const getAllBookings = async (params?: {
  skip?: number;
  limit?: number;
  status_filter?: string;
  date_from?: string;
  date_to?: string;
  needs_admin_review?: boolean;
}): Promise<BookingAdmin[]> => {
  const response = await apiClient.get('/api/v1/admin/bookings', { params });
  return response.data;
};

export const updateBookingAdmin = async (
  bookingId: number,
  updates: any,
): Promise<BookingAdmin> => {
  const response = await apiClient.put(`/api/v1/admin/bookings/${bookingId}`, updates);
  return response.data;
};

/**
 * Cancel a booking as an admin, moving exactly the amount asked for.
 *
 * `refundAmountCents` is the money this call moves: omit it to refund the
 * whole remaining refundable balance, pass 0 to close the booking without
 * moving anything, or pass an amount at or below the remaining balance.
 */
export const cancelBookingAdmin = async (
  bookingId: number,
  reason: string,
  refundAmountCents?: number,
): Promise<{
  message: string;
  refund_issued: boolean;
  refund_failed: boolean;
  refund_amount_cents: number;
  refund_failed_cents?: number;
}> => {
  const response = await apiClient.post(`/api/v1/admin/bookings/${bookingId}/cancel`, null, {
    params:
      refundAmountCents === undefined
        ? { reason }
        : { reason, refund_amount_cents: refundAmountCents },
  });
  return response.data;
};

export const getAnalyticsOverview = async (days: number = 30): Promise<AnalyticsOverview> => {
  const response = await apiClient.get('/api/v1/admin/analytics/overview', { params: { days } });
  return response.data;
};

export const getBookingsChartData = async (days: number = 30): Promise<BookingChartData[]> => {
  const response = await apiClient.get('/api/v1/admin/analytics/bookings-chart', {
    params: { days },
  });
  return response.data;
};

// Review Moderation (Admin)
export const getPendingReviews = async (skip?: number, limit?: number): Promise<Review[]> => {
  const response = await apiClient.get('/api/v1/reviews/pending', { params: { skip, limit } });
  return response.data;
};

export const moderateReview = async (
  reviewId: number,
  action: 'approve' | 'reject',
): Promise<Review> => {
  const response = await apiClient.put(`/api/v1/reviews/${reviewId}/moderate`, null, {
    params: { action },
  });
  return response.data;
};

// Notification Endpoints
export const getUserNotifications = async (
  skip?: number,
  limit?: number,
): Promise<Notification[]> => {
  const response = await apiClient.get('/api/v1/notifications', { params: { skip, limit } });
  return response.data;
};

export const markNotificationAsRead = async (notificationId: number): Promise<void> => {
  await apiClient.put(`/api/v1/notifications/${notificationId}/read`);
};

export const markAllNotificationsAsRead = async (category?: string): Promise<void> => {
  await apiClient.put('/api/v1/notifications/mark-all-read', { category });
};

export interface EmailMarketingConsentBody {
  opted_in: boolean;
  disclosure_version: string;
}

export const updateEmailMarketingConsent = async (
  body: EmailMarketingConsentBody,
): Promise<User> => {
  const response = await apiClient.put('/api/v1/users/me/email-marketing-consent', body);
  return response.data;
};

export const answerEmailMarketingPrompt = async (
  body: EmailMarketingConsentBody,
): Promise<User> => {
  const response = await apiClient.post('/api/v1/users/me/email-marketing-prompt', body);
  return response.data;
};

// Payment Endpoints
export const getStripePublishableKey = async (): Promise<string> => {
  const response = await apiClient.get('/api/v1/payments/config');
  return response.data.publishable_key;
};

// Service Wishlist functions
export const addServiceToWishlist = async (serviceId: number): Promise<any> => {
  const response = await apiClient.post('/api/v1/wishlist/service', { service_id: serviceId });
  return response.data;
};

export const removeServiceFromWishlist = async (serviceId: number): Promise<any> => {
  const response = await apiClient.delete(`/api/v1/wishlist/service/${serviceId}`);
  return response.data;
};

// Amenities Endpoints
export interface Amenity {
  id: number;
  name: string;
  category: string;
  icon?: string;
  description?: string;
}

export interface AmenityCategory {
  category: string;
  count: number;
}

export const getAmenities = async (category?: string): Promise<Amenity[]> => {
  const response = await apiClient.get('/api/v1/amenities', {
    params: category ? { category } : {},
  });
  return response.data;
};

export const getAmenityCategories = async (): Promise<AmenityCategory[]> => {
  const response = await apiClient.get('/api/v1/amenities/categories');
  return response.data;
};

export const getAmenitiesGrouped = async (): Promise<Record<string, Amenity[]>> => {
  const response = await apiClient.get('/api/v1/amenities/grouped');
  return response.data;
};

/** Admin-only: assign a catalog amenity its curated icon slug. */
export const updateAmenityIcon = async (amenityId: number, icon: string): Promise<Amenity> => {
  const response = await apiClient.patch(`/api/v1/admin/amenities/${amenityId}`, { icon });
  return response.data;
};

// Admin refund types — mirror backend/schemas/refund.py shapes.

/** Why the admin Refund action is refused for a metered booking, served by the GET refunds list. */
export type RefundBlockCode =
  | 'REFUND_CHARGE_IN_PROGRESS'
  | 'REFUND_AMOUNT_DUE'
  | 'REFUND_APPROVAL_OUTSTANDING'
  | 'REFUND_BEFORE_ACTUALS';

/** A single refund ledger row, as returned by the GET refunds list endpoint. */
export interface RefundRecord {
  id: number;
  amount_cents: number;
  reason: string | null;
  status: string;
  stripe_refund_id: string | null;
  stripe_payment_intent_id: string | null;
  /** Rows written by one admin refund share a group; historical rows carry null. */
  refund_group_id: string | null;
  /** True for a leg closed as FAILED without ever being sent to Stripe. */
  not_attempted: boolean;
  admin_email: string | null;
  created_at: string;
}

/**
 * Response from POST /api/v1/admin/bookings/{id}/refund — the refund group
 * that was issued (one leg per refunded charge) plus updated refundable
 * remainder and payment_status so the caller can refresh without a separate
 * GET. `id` and `stripe_refund_id` are set only for a single-leg refund.
 */
export interface BookingRefundResponse {
  refund_group_id: string;
  id: number | null;
  amount_cents: number;
  reason: string | null;
  status: string;
  stripe_refund_id: string | null;
  admin_email: string | null;
  created_at: string;
  legs: RefundRecord[];
  refundable_remainder_cents: number;
  payment_status: string;
}

/** Response from GET /api/v1/admin/bookings/{id}/refunds. */
export interface BookingRefundListResponse {
  refunds: RefundRecord[];
  refundable_remainder_cents: number;
  refund_basis_cents: number;
  /** Absent from an older backend; read as null. */
  refund_block_code?: RefundBlockCode | null;
  /** Why a standalone refund is refused; absent from an older backend, which falls back to `refund_block_code`. */
  standalone_refund_block_code?: RefundBlockCode | null;
}

/**
 * Issue a partial or full refund against an admin booking.
 *
 * `body.amount_cents` — omit for a full remainder refund; pass a value in
 * cents for a partial refund. `body.reason` is optional free text logged on
 * the refund record.
 *
 * The endpoint rejects with 400 when the booking has no payment intent, 409
 * when a refund is already in progress, 422 when the booking is ineligible or
 * the amount exceeds the remainder, and 502 on a Stripe-side failure.
 */
export const issueBookingRefund = async (
  bookingId: number,
  body: { amount_cents?: number; reason?: string },
): Promise<BookingRefundResponse> => {
  const response = await apiClient.post(`/api/v1/admin/bookings/${bookingId}/refund`, body);
  return response.data;
};

/** Fetch the full refund history and refundable remainder for a booking. */
export const getBookingRefunds = async (bookingId: number): Promise<BookingRefundListResponse> => {
  const response = await apiClient.get(`/api/v1/admin/bookings/${bookingId}/refunds`);
  return response.data;
};

// Export the configured apiClient for direct use
export { apiClient };

/**
 * Mint a Stripe SetupIntent for the native-only cart card-save path. The
 * frontend mounts Stripe Elements against the returned `client_secret` and
 * confirms the SetupIntent; the resulting PaymentMethod id (pm_…) becomes
 * the `saved_payment_method_id` sent to checkout-cart.
 */
export const createSetupIntent = async (): Promise<SetupIntentResponse> => {
  const response = await apiClient.post('/api/v1/payments/setup-intent');
  return response.data;
};

/**
 * Submit a mixed or native-only cart to the checkout-cart endpoint.
 *
 * The caller is responsible for:
 * - assembling the `idempotency_key` (use `crypto.randomUUID()`)
 * - the `saved_payment_method_id` (must be a pm_… from the SetupIntent or
 *   PaymentIntent confirmation — never a pi_… id)
 * - the `consent_text_version` (must match the served constant from
 *   `constants/offSessionConsent.ts`)
 * - the per-line `items` array
 *
 * Returns a `CartCheckoutResponse` with per-line typed outcomes. A 422
 * response indicates a consent-version mismatch — the caller must re-render
 * the consent step with the current copy and re-submit with the SAME
 * idempotency key (do not mint a new one on retry).
 */
export const checkoutCart = async (request: CartCheckoutRequest): Promise<CartCheckoutResponse> => {
  const response = await apiClient.post('/api/v1/bookings/checkout-cart', request);
  return response.data;
};

/**
 * Fetch the SCA recovery PaymentIntent details for a booking parked in
 * the SCA-recovery state. Returns the client_secret and payment_method_id
 * required to drive `stripe.confirmCardPayment` on-session.
 *
 * Mirrors `backend/schemas/booking.py::RecoveryIntentResponse`.
 */
export const getBookingRecoveryIntent = async (
  bookingId: number,
): Promise<RecoveryIntentResponse> => {
  const response = await apiClient.get(`/api/v1/bookings/${bookingId}/recovery-intent`);
  return response.data;
};

/**
 * Mint an on-session PaymentIntent for the metered balance the guest still
 * owes. Re-minting cancels the previously minted open intent. Answers 409
 * with a typed detail when approval is outstanding, nothing is due, or a
 * payment is already in progress.
 *
 * Mirrors `backend/schemas/booking.py::DeltaPaymentIntentResponse`.
 */
export const createDeltaPaymentIntent = async (
  bookingId: number,
): Promise<DeltaPaymentIntentResponse> => {
  const response = await apiClient.post(`/api/v1/bookings/${bookingId}/delta-payment-intent`);
  return response.data;
};

/**
 * Read-through poll after a `stripe.confirmCardPayment` call. Reflects
 * the current persisted booking state alongside the live Stripe intent
 * status. Does NOT flip any state — webhook settlement is authoritative.
 *
 * Mirrors `backend/schemas/booking.py::SettleRecoveryResponse`.
 */
export const settleBookingRecovery = async (bookingId: number): Promise<SettleRecoveryResponse> => {
  const response = await apiClient.post(`/api/v1/bookings/${bookingId}/settle-recovery`);
  return response.data;
};

/**
 * Retry a hard-declined off-session charge using the saved payment method.
 * Only valid when the booking is in the hard-declined state.
 *
 * Mirrors `backend/schemas/booking.py::RetryPaymentResponse`.
 */
export const retryPayment = async (bookingId: number): Promise<RetryPaymentResponse> => {
  const response = await apiClient.post(`/api/v1/bookings/${bookingId}/retry-payment`);
  return response.data;
};

/**
 * Approve a pending charge for a booking where a customer approval is
 * required before the charge fires. `approvedAmountCents` must be the exact
 * amount the customer was shown on the approval page — the server compares
 * it against the booking's current charge amount and rejects a mismatch
 * with 409 rather than charging a number the customer never saw. The
 * endpoint records the approval and fires the off-session charge inline
 * through the orchestrator, returning the charge outcome so the caller can
 * surface the real result to the customer.
 */
export const approveReprice = async (
  bookingId: number,
  approvedAmountCents: number,
): Promise<import('../types').ApproveRepriceResponse> => {
  const request: import('../types').ApproveRepriceRequest = {
    approved_amount_cents: approvedAmountCents,
  };
  const response = await apiClient.post<import('../types').ApproveRepriceResponse>(
    `/api/v1/bookings/${bookingId}/approve-reprice`,
    request,
  );
  return response.data;
};

/**
 * Submit a public, unauthenticated concierge enquiry. The camelCase → snake_case
 * wire mapping lives here — the page keeps the camelCase draft its JSX reads
 * naturally. Optional fields left blank are sent as `undefined` (dropped from
 * the JSON body) rather than as empty strings, so the server's own optional-field
 * defaults apply exactly as if the guest never touched the control.
 *
 * A non-2xx response (422/429/500/502) rejects the promise; the page renders
 * its own failure copy and never surfaces the backend's `detail` text.
 */
export const submitEnquiry = async (draft: EnquiryDraft): Promise<EnquiryResponse> => {
  const request: EnquiryCreateRequest = {
    name: draft.name,
    email: draft.email,
    description: draft.description,
    arrival: draft.arrival || undefined,
    departure: draft.departure || undefined,
    party_size: draft.partySize ? Number(draft.partySize) : undefined,
    budget: draft.budget || undefined,
    company: draft.company || undefined,
  };
  const response = await apiClient.post<EnquiryResponse>('/api/v1/enquiries/', request);
  return response.data;
};

/**
 * Submit a public, unauthenticated supply-side partner application from the
 * Join page. The camelCase → snake_case wire mapping lives here — the page
 * keeps the camelCase draft its JSX reads naturally. Optional fields left
 * blank are sent as `undefined` (dropped from the JSON body) rather than as
 * empty strings, so the server's own optional-field defaults apply exactly
 * as if the applicant never touched the control.
 *
 * A non-2xx response (422/429/500/502) rejects the promise; the page renders
 * its own failure copy and never surfaces the backend's `detail` text.
 */
export const submitPartnerApplication = async (
  draft: PartnerApplicationDraft,
): Promise<PartnerApplicationResponse> => {
  const request: PartnerApplicationCreateRequest = {
    name: draft.name,
    email: draft.email,
    category: draft.category,
    business_name: draft.businessName || undefined,
    phone: draft.phone || undefined,
    website: draft.website || undefined,
    message: draft.message || undefined,
    company: draft.company || undefined,
  };
  const response = await apiClient.post<PartnerApplicationResponse>(
    '/api/v1/partner-applications/',
    request,
  );
  return response.data;
};

/**
 * Fetch Belhop's own aggregate Google rating plus its most recent reviews
 * (public, unauthenticated GET, sibling to the enquiry POST above). Always
 * resolves with HTTP 200 — an upstream failure or an unconfigured place_id
 * comes back as an empty `reviews` array rather than a rejected promise, so
 * callers only need to branch on `reviews.length`.
 */
export const getCompanyReviews = async (): Promise<CompanyReviewsResponse> => {
  const response = await apiClient.get<CompanyReviewsResponse>('/api/v1/enquiries/google-reviews');
  return response.data;
};

/** Public blog post by slug. A draft or unknown slug rejects with a 404. */
export const getBlogPost = async (slug: string): Promise<BlogPost> => {
  const response = await apiClient.get<BlogPost>(`/api/v1/blog/posts/${encodeURIComponent(slug)}`);
  return response.data;
};

/** One page of published blog posts, newest first. */
export const getBlogPosts = async (page: number): Promise<BlogPostPage> => {
  const response = await apiClient.get<BlogPostPage>('/api/v1/blog/posts', { params: { page } });
  return response.data;
};

const ADMIN_BLOG_POSTS_PATH = '/api/v1/admin/blog/posts';

/** Every blog post, drafts included, newest edit first. */
export const getAdminBlogPosts = async (): Promise<BlogPostAdminSummary[]> => {
  const response = await apiClient.get<BlogPostAdminSummary[]>(ADMIN_BLOG_POSTS_PATH);
  return response.data;
};

/** Creates a draft from a title; the server derives the slug. */
export const createBlogPost = async (body: { title: string }): Promise<BlogPostAdmin> => {
  const response = await apiClient.post<BlogPostAdmin>(ADMIN_BLOG_POSTS_PATH, body);
  return response.data;
};

export const getAdminBlogPost = async (postId: number): Promise<BlogPostAdmin> => {
  const response = await apiClient.get<BlogPostAdmin>(`${ADMIN_BLOG_POSTS_PATH}/${postId}`);
  return response.data;
};

/** Partial update: send only the changed fields. */
export const updateBlogPost = async (
  postId: number,
  changes: BlogPostUpdate,
): Promise<BlogPostAdmin> => {
  const response = await apiClient.patch<BlogPostAdmin>(
    `${ADMIN_BLOG_POSTS_PATH}/${postId}`,
    changes,
  );
  return response.data;
};

/**
 * Publishes a draft. Omit `publishedAt` to let the server use its own clock
 * (or keep a stored date on a re-publish); otherwise pass a UTC ISO instant.
 */
export const publishBlogPost = async (
  postId: number,
  publishedAt?: string,
): Promise<BlogPostAdmin> => {
  const response = await apiClient.post<BlogPostAdmin>(
    `${ADMIN_BLOG_POSTS_PATH}/${postId}/publish`,
    publishedAt === undefined ? {} : { published_at: publishedAt },
  );
  return response.data;
};

/** Takes a live post back to draft; the slug and published date are kept. */
export const unpublishBlogPost = async (postId: number): Promise<BlogPostAdmin> => {
  const response = await apiClient.post<BlogPostAdmin>(
    `${ADMIN_BLOG_POSTS_PATH}/${postId}/unpublish`,
  );
  return response.data;
};

/** Deletes a never-published draft. */
export const deleteBlogPost = async (postId: number): Promise<void> => {
  await apiClient.delete(`${ADMIN_BLOG_POSTS_PATH}/${postId}`);
};

export const getAdminHeroSlots = async (): Promise<AdminHeroSlot[]> => {
  const response = await apiClient.get('/api/v1/admin/heroes');
  return response.data;
};

export const setHeroSlot = async (
  slot: HeroSlotName,
  body: HeroSlotUpdate,
): Promise<AdminHeroSlot> => {
  const response = await apiClient.put(`/api/v1/admin/heroes/${slot}`, body);
  return response.data;
};

export const clearHeroSlot = async (slot: HeroSlotName): Promise<void> => {
  await apiClient.delete(`/api/v1/admin/heroes/${slot}`);
};

/** Public: the admin's hero picks per slot, `null` where unset or no longer live. */
export const getHeroPicks = async (): Promise<PublicHeroPicks> => {
  const response = await apiClient.get<PublicHeroPicks>('/api/v1/heroes');
  return response.data;
};

const ADMIN_BLOG_PATH = '/api/v1/admin/blog';

/** A post by slug at any status, for an admin preview. */
export const getAdminBlogPostPreview = async (slug: string): Promise<BlogPostPreview> => {
  const response = await apiClient.get<BlogPostPreview>(
    `${ADMIN_BLOG_PATH}/preview/${encodeURIComponent(slug)}`,
  );
  return response.data;
};

/** The topic queue: queued topics oldest first, then drafted and discarded ones. */
export const getBlogTopics = async (): Promise<BlogTopic[]> => {
  const response = await apiClient.get<BlogTopic[]>(`${ADMIN_BLOG_PATH}/topics`);
  return response.data;
};

export const createBlogTopic = async (body: BlogTopicCreate): Promise<BlogTopic> => {
  const response = await apiClient.post<BlogTopic>(`${ADMIN_BLOG_PATH}/topics`, body);
  return response.data;
};

export const discardBlogTopic = async (topicId: number): Promise<BlogTopic> => {
  const response = await apiClient.post<BlogTopic>(`${ADMIN_BLOG_PATH}/topics/${topicId}/discard`);
  return response.data;
};

/** Asks `sync_runner` to draft this topic; resolves with the queued run. */
export const requestBlogTopicDraft = async (topicId: number): Promise<BlogDraftRun> => {
  const response = await apiClient.post<BlogDraftRun>(`${ADMIN_BLOG_PATH}/topics/${topicId}/draft`);
  return response.data;
};

/** Asks `sync_runner` to draft a post on a topic the drafter chooses; resolves with the queued run. */
export const requestOpenBlogDraft = async (): Promise<BlogDraftRun> => {
  const response = await apiClient.post<BlogDraftRun>(`${ADMIN_BLOG_PATH}/draft-runs`);
  return response.data;
};

/** Estimated drafting spend this month. */
export const getBlogDraftCostSummary = async (): Promise<BlogDraftCostSummary> => {
  const response = await apiClient.get<BlogDraftCostSummary>(`${ADMIN_BLOG_PATH}/draft-costs`);
  return response.data;
};

/** The 20 most recent draft runs, newest first. */
export const getBlogDraftRuns = async (): Promise<BlogDraftRun[]> => {
  const response = await apiClient.get<BlogDraftRun[]>(`${ADMIN_BLOG_PATH}/draft-runs`);
  return response.data;
};
